import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Filter,
  MoreVertical,
  Link2,
  Calendar,
  CheckCircle2,
  Circle,
  PauseCircle,
  AlertCircle,
  AlertTriangle,
  X,
  Settings,
  ArrowLeft,
  GripVertical,
  Play,
  Pause,
  Zap,
  Plus,
  Search,
  Check,
  CheckCircle
} from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, isToday, isSameDay, parseISO, differenceInDays, isTomorrow, isThisWeek } from 'date-fns';
import { useCollegeContext } from '../../../contexts/CollegeContext';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { supabase } from '../../../lib/supabase';
import { toast } from 'react-hot-toast';
import timelineService from '../../../services/timelineService';
import { parseLocalDate } from '../../../utils/dateUtils';
import TaskManagerPanel from '../TaskManagerPanel';
import TaskSynchronizationService from '../../../services/TaskSynchronizationService';
import smartActionsIntelligence from '../../../services/smartActionsIntelligence';
import SmartInsightsService from '../../../services/SmartInsightsService';
import SmartActionPreview from '../SmartActionPreview';
import AdaptiveTimelineSupport from '../AdaptiveTimelineSupport';
import TaskBatchingService from '../../../services/TaskBatchingService.js';
import BatchTaskCard from '../BatchTaskCard';
import { useSessionContext } from '../../../contexts/SessionContext';
import { CompletionRuleEvaluator } from '../../../services/CompletionRuleEvaluator';
import RecoveryPlanPreview from '../RecoveryPlanPreview';
import RecoveryPlanErrorBoundary from '../RecoveryPlanErrorBoundary';
import RecoveryPlanService from '../../../services/RecoveryPlanService';
import { useGlobalDeadlines } from '../../../hooks/useGlobalDeadlines';
import { useTimelineTasks } from '../../../hooks/useTimelineTasks';
import OverdueTaskCard from '../shared/OverDueTaskCard';
import QuickTaskAdd from '../QuickTaskAdd';
import { getTaskType, handleTaskAction } from '../../../utils/taskTypeUtils';
import CrisisSupport from '../../Intelligence/CrisisSupport';
import HybridIntelligenceRouter from '../../../services/intelligence/HybridIntelligenceRouter';
import HierarchicalView from '../../Timeline/HierarchicalView';
import CollegesView from '../../Timeline/CollegesView';
import UpcomingDeadlines from '../../Timeline/UpcomingDeadlines';

// Helper function to format hours to 2 decimal places
const formatHours = (hours) => {
  if (!hours) return 0;
  return Math.round(hours * 100) / 100;
};

// Category mapping for task categorization
const CATEGORIES = {
  'Profile & Foundation': {
    key: 'profile',
    color: 'bg-pink-50 text-pink-800',
    icon: '👤',
    label: 'Profile & Foundation'
  },
  'College Discovery': {
    key: 'research',
    color: 'bg-azure-50 text-azure-800',
    icon: '🔍',
    label: 'School Research'
  },
  'Testing Strategy': {
    key: 'testing',
    color: 'bg-violet-50 text-violet-800',
    icon: '📊',
    label: 'Testing Strategy'
  },
  'Essay Development': {
    key: 'essay',
    color: 'bg-amber-50 text-amber-800',
    icon: '📝',
    label: 'Essays & Writing'
  },
  'Financial Planning': {
    key: 'financial',
    color: 'bg-emerald-50 text-emerald-800',
    icon: '💰',
    label: 'Financial Aid'
  },
  'Application Execution': {
    key: 'admin',
    color: 'bg-violet-50 text-violet-800',
    icon: '📋',
    label: 'Applications'
  },
  'Decision & Enrollment': {
    key: 'decision',
    color: 'bg-indigo-50 text-indigo-800',
    icon: '🎓',
    label: 'Decisions'
  }
};

// All view component - extracted outside to prevent recreation on parent re-renders
const AllView = React.memo(({
  timeline,
  allViewTab,
  setAllViewTab,
  searchTerm,
  setSearchTerm,
  filterCategory,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  selectedTasks,
  setSelectedTasks,
  toggleTaskStatus,
  refetchTimeline,
  activeSession
}) => {
  console.log('🔧 [DEBUG] AllView component rendering', { searchTerm, timelineLength: timeline?.length });

  const searchInputRef = useRef(null);

  // Check if task is completed
  const isTaskCompleted = (task) => {
    return task.completed || task.completion_status === 'completed' || task.status === 'completed' || task.workflow_phase === 'complete';
  };

  // Check if task is overdue
  const isTaskOverdue = (task) => {
    return task.scheduled_date &&
           parseISO(task.scheduled_date) < new Date() &&
           !isTaskCompleted(task) &&
           task.status !== 'dismissed';
  };

  // Get category info
  const getCategoryInfo = (category) => {
    const categoryEntry = Object.values(CATEGORIES).find(c => c.key === category);
    return categoryEntry || { color: 'bg-slate-100 text-slate-700', icon: '📋', label: 'General' };
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      'critical': 'text-red-700 bg-red-100 border border-red-200 font-semibold',
      'high': 'text-orange-700 bg-orange-100 border border-orange-200 font-medium',
      'medium': 'text-azure-700 bg-azure-100 border border-azure-200 font-medium',
      'low': 'text-slate-700 bg-stone-100 border border-stone-200 font-normal'
    };
    return colors[priority] || colors['medium'];
  };

  // Get date display
  const getDateDisplay = (task) => {
    if (isTaskCompleted(task) && task.completed_at) {
      const completedDate = parseISO(task.completed_at);
      return `Completed ${format(completedDate, 'MMM d')}`;
    }

    const date = task.scheduled_date || task.due_date;
    if (!date) return 'No date set';

    const parsedDate = parseISO(date);
    if (isToday(parsedDate)) return 'Today';
    if (isTomorrow(parsedDate)) return 'Tomorrow';
    if (isThisWeek(parsedDate)) return format(parsedDate, 'EEEE');
    return format(parsedDate, 'MMM d');
  };

  // Calculate days late for overdue tasks
  const getDaysLate = (task) => {
    const date = task.scheduled_date || task.due_date;
    if (!date) return 0;

    const taskDate = parseISO(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    taskDate.setHours(0, 0, 0, 0);

    if (taskDate >= today) return 0;

    const diffTime = today.getTime() - taskDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filter and sort tasks
  const filteredAndSortedTasks = useMemo(() => {
    console.log('🔧 [DEBUG] AllView: filteredAndSortedTasks recalculating');
    let filtered = timeline.filter(task => {
      // Tab filtering
      if (allViewTab === 'ready') return task.completion_status === 'not_started' && task.status !== 'dismissed';
      if (allViewTab === 'progress') return task.completion_status === 'in_progress' || task.status === 'in_progress';
      if (allViewTab === 'completed') return task.completion_status === 'completed';
      if (allViewTab === 'dismissed') return task.status === 'dismissed';
      if (allViewTab === 'overdue') return isTaskOverdue(task);
      if (allViewTab === 'all') return task.status !== 'dismissed' && !isTaskCompleted(task);

      return true;
    });

    // Search filtering
    if (searchTerm) {
      filtered = filtered.filter(task =>
        task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.college_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filtering
    if (filterCategory !== 'all') {
      filtered = filtered.filter(task => task.category === filterCategory);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'due_date':
          aVal = a.scheduled_date || a.due_date || '9999-12-31';
          bVal = b.scheduled_date || b.due_date || '9999-12-31';
          break;
        case 'title':
          aVal = a.title || '';
          bVal = b.title || '';
          break;
        case 'priority': {
          const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
          aVal = priorityOrder[a.priority] || 0;
          bVal = priorityOrder[b.priority] || 0;
          break;
        }
        case 'category':
          aVal = a.category || '';
          bVal = b.category || '';
          break;
        default:
          aVal = a.created_at || '';
          bVal = b.created_at || '';
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return filtered;
  }, [timeline, allViewTab, searchTerm, filterCategory, sortBy, sortOrder]);

  // Task counts for tabs
  const taskCounts = useMemo(() => {
    return {
      all: timeline.filter(t => t.status !== 'dismissed' && !isTaskCompleted(t)).length,
      ready: timeline.filter(t => t.completion_status === 'not_started' && t.status !== 'dismissed').length,
      progress: timeline.filter(t => t.completion_status === 'in_progress' || t.status === 'in_progress').length,
      completed: timeline.filter(t => t.completed === true || t.completion_status === 'completed').length,
      dismissed: timeline.filter(t => t.status === 'dismissed').length,
      overdue: timeline.filter(t => isTaskOverdue(t)).length
    };
  }, [timeline]);

  // Toggle task selection
  const toggleTaskSelection = (taskId) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  // Select all visible tasks
  const selectAllVisible = () => {
    const visibleTaskIds = filteredAndSortedTasks.map(task => task.id);
    setSelectedTasks(new Set(visibleTaskIds));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedTasks(new Set());
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (selectedTasks.size === 0) {
      toast.error('No tasks selected');
      return;
    }

    try {
      const taskIds = Array.from(selectedTasks);

      for (const taskId of taskIds) {
        if (action === 'complete') {
          await toggleTaskStatus(taskId);
        } else if (action === 'dismiss') {
          // Handle dismiss action
          const updates = { status: 'dismissed', dismissed_at: new Date().toISOString() };
          await TaskSynchronizationService.updateFromEpic(taskId, updates);
        }
      }

      setSelectedTasks(new Set());
      toast.success(`${taskIds.length} tasks updated`);
      refetchTimeline(); // Refresh timeline

    } catch (error) {
      console.error('Error with bulk action:', error);
      toast.error('Failed to update tasks');
    }
  };

  // Memoize search handler to prevent input focus loss
  const handleSearchChange = useCallback((e) => {
    console.log('🔧 [DEBUG] AllView search onChange', e.target.value);
    setSearchTerm(e.target.value);
  }, [setSearchTerm]);

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {[
          { key: 'all', label: 'All Tasks', count: taskCounts.all },
          { key: 'overdue', label: 'Overdue', count: taskCounts.overdue },
          { key: 'ready', label: 'Ready to Start', count: taskCounts.ready },
          { key: 'progress', label: 'In Progress', count: taskCounts.progress },
          { key: 'completed', label: 'Completed', count: taskCounts.completed },
          { key: 'dismissed', label: 'Dismissed', count: taskCounts.dismissed }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setAllViewTab(tab.key)}
            className={`px-3 py-2 text-sm rounded-lg font-medium transition-colors whitespace-nowrap ${
              allViewTab === tab.key
                ? tab.key === 'overdue'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-azure-100 text-azure-700'
                : tab.key === 'overdue' && tab.count > 0
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-slate-600 hover:bg-stone-100'
            }`}
          >
            {tab.label}
            {tab.count !== null && (
              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                tab.key === 'overdue' && tab.count > 0
                  ? 'bg-red-200 text-red-800'
                  : 'bg-stone-200 text-slate-700'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search and Controls */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400 w-4 h-4 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
            autoComplete="off"
          />
        </div>

        <select
          value={`${sortBy}-${sortOrder}`}
          onChange={(e) => {
            const [field, order] = e.target.value.split('-');
            setSortBy(field);
            setSortOrder(order);
          }}
          className="px-4 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
        >
          <option value="due_date-asc">Due Date (Earliest)</option>
          <option value="due_date-desc">Due Date (Latest)</option>
          <option value="title-asc">Title (A-Z)</option>
          <option value="title-desc">Title (Z-A)</option>
          <option value="priority-desc">Priority (High-Low)</option>
          <option value="priority-asc">Priority (Low-High)</option>
          <option value="category-asc">Category</option>
        </select>
      </div>

      {/* Bulk Actions */}
      {selectedTasks.size > 0 && (
        <div className="flex items-center gap-4 p-3 bg-azure-50 rounded-lg">
          <span className="text-sm font-medium text-azure-900">
            {selectedTasks.size} task{selectedTasks.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => handleBulkAction('complete')}
              className="px-3 py-1 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700"
            >
              Complete
            </button>
            <button
              onClick={() => handleBulkAction('dismiss')}
              className="px-3 py-1 bg-slate-600 text-white text-sm rounded hover:bg-slate-700"
            >
              Dismiss
            </button>
            <button
              onClick={clearSelection}
              className="px-3 py-1 bg-stone-200 text-slate-700 text-sm rounded hover:bg-stone-300"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Task List */}
      {filteredAndSortedTasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-stone-500">
          <AlertCircle className="w-12 h-12 mb-4 text-stone-300" />
          <h3 className="text-lg font-medium mb-2">No tasks found</h3>
          <p className="text-sm">Try adjusting your filters or search terms</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Select All */}
          <div className="flex items-center gap-3 p-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={selectedTasks.size === filteredAndSortedTasks.length}
              onChange={() => {
                if (selectedTasks.size === filteredAndSortedTasks.length) {
                  clearSelection();
                } else {
                  selectAllVisible();
                }
              }}
              className="w-4 h-4"
            />
            <span>Select all ({filteredAndSortedTasks.length} tasks)</span>
          </div>

          {/* Task Items */}
          {filteredAndSortedTasks.map((task) => {
            const categoryInfo = getCategoryInfo(task.category);
            const isSelected = selectedTasks.has(task.id);
            const isCompleted = isTaskCompleted(task);
            const isOverdue = isTaskOverdue(task);
            const daysLate = getDaysLate(task);

            return (
              <div
                key={task.id}
                className={`p-4 border rounded-lg transition-all duration-200 ${
                  isCompleted
                    ? isSelected
                      ? 'bg-stone-100 border-stone-300 shadow-sm opacity-75'
                      : 'bg-stone-50 border-stone-200 hover:bg-stone-100 opacity-75'
                    : isSelected
                      ? isOverdue
                        ? 'bg-red-50 border-red-300 shadow-sm'
                        : 'bg-azure-50 border-azure-200 shadow-sm'
                      : isOverdue
                        ? 'bg-red-25 border-red-200 hover:bg-red-50 hover:shadow-sm'
                        : 'bg-white border-stone-200 hover:bg-stone-50 hover:shadow-sm'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleTaskSelection(task.id)}
                    className="mt-1 w-4 h-4"
                  />

                  {/* Task Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold truncate leading-tight ${
                      isCompleted ? 'text-stone-500 line-through' : isOverdue ? 'text-red-900' : 'text-slate-900'
                    }`}>
                      {task.title}
                    </h4>

                    {task.college_name && (
                      <p className="text-xs text-slate-800 mt-0.5 font-medium">
                        {task.college_name}
                      </p>
                    )}

                    {task.description && (
                      <p className={`text-sm mt-1.5 line-clamp-2 leading-relaxed ${
                        isCompleted ? 'text-stone-400' : isOverdue ? 'text-red-700' : 'text-slate-600'
                      }`}>
                        {task.description}
                      </p>
                    )}

                    {/* Metadata */}
                    <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                      <span className={`px-2 py-1 rounded ${categoryInfo.color}`}>
                        {categoryInfo.label}
                      </span>

                      <span className={`px-2 py-1 rounded ${getPriorityColor(task.priority)}`}>
                        {task.priority || 'medium'}
                      </span>

                      <span className="text-slate-800 font-medium">
                        ⏱ {formatHours(task.estimated_hours || 0.5)}h
                      </span>

                      {isOverdue && daysLate > 0 && (
                        <span className="px-2 py-1 rounded bg-red-200 text-red-800 font-bold">
                          {daysLate} DAY{daysLate > 1 ? 'S' : ''} LATE
                        </span>
                      )}

                      <span className={`flex items-center gap-1 ${
                        isOverdue ? 'text-red-600 font-semibold' : 'text-slate-700'
                      }`}>
                        <Calendar className="w-3 h-3" />
                        {getDateDisplay(task)}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {!isCompleted && (
                      <button
                        onClick={() => toggleTaskStatus(task.id)}
                        className="p-2 text-emerald-600 hover:bg-emerald-100 rounded transition-colors"
                        title="Mark as complete"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    {isCompleted && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs font-medium">Done</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

AllView.displayName = 'AllView';

const UnifiedTimelineIntegrated = () => {
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();
  const { colleges } = useCollegeContext();

  // DEBUG: Track parent component renders
  const parentRenderCount = useRef(0);
  parentRenderCount.current += 1;
  console.log('🔧 [DEBUG] UnifiedTimelineIntegrated RENDER #', parentRenderCount.current);

  // DEBUG: Track what's causing re-renders
  const prevPropsRef = useRef({ currentUser, colleges });
  useEffect(() => {
    const prev = prevPropsRef.current;
    const changes = [];

    if (prev.currentUser !== currentUser) changes.push('currentUser');
    if (prev.colleges !== colleges) changes.push('colleges');

    if (changes.length > 0) {
      console.log('🔧 [DEBUG] UnifiedTimelineIntegrated re-rendered due to prop changes:', changes);
    }

    prevPropsRef.current = { currentUser, colleges };
  });

  // Use shared timeline hook (handles fetching, transformation, completion rules)
  const { timeline: rawTimeline, loading: timelineLoading, refetch: refetchTimeline } = useTimelineTasks(
    currentUser?.id,
    colleges
  );

  // DEBUG: Track when rawTimeline reference changes
  const rawTimelineRef = useRef(rawTimeline);
  useEffect(() => {
    if (rawTimeline !== rawTimelineRef.current) {
      console.log('🔧 [DEBUG] UnifiedTimelineIntegrated: rawTimeline reference CHANGED', {
        oldLength: rawTimelineRef.current?.length,
        newLength: rawTimeline?.length,
        isNewReference: rawTimeline !== rawTimelineRef.current
      });
      rawTimelineRef.current = rawTimeline;
    }
  }, [rawTimeline]);

  // State
  const [currentView, setCurrentView] = useState('focused'); // focused, colleges, weekly, all
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterRound, setFilterRound] = useState('all'); // all, ed, ea, rd
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [draggedTask, setDraggedTask] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(null);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [showRescheduleMenu, setShowRescheduleMenu] = useState(null);
  const [reschedulingTaskId, setReschedulingTaskId] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerTaskId, setDatePickerTaskId] = useState(null);
  const [timeline, setTimeline] = useState([]); // Keep as state for local updates
  const [batchedTimeline, setBatchedTimeline] = useState([]); // For display with batches
  const [showTaskManager, setShowTaskManager] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [showInsightPreview, setShowInsightPreview] = useState(null);
  const [hasInitializedDate, setHasInitializedDate] = useState(false);
  const [batchingEnabled, setBatchingEnabled] = useState(false); // Toggle for batching - DISABLED by default
  const [showRecoveryPlan, setShowRecoveryPlan] = useState(null);
  const [showTaskCreator, setShowTaskCreator] = useState(false);
  const [showAllOverdueTasks, setShowAllOverdueTasks] = useState(false);
  const [showCrisisSupport, setShowCrisisSupport] = useState(false);
  const [crisisLevel, setCrisisLevel] = useState('normal');

  // AllView states
  const [selectedTasks, setSelectedTasks] = useState(new Set());

  // DEBUG: Track when timeline state changes
  const timelineStateRef = useRef(timeline);
  useEffect(() => {
    if (timeline !== timelineStateRef.current) {
      console.log('🔧 [DEBUG] UnifiedTimelineIntegrated: timeline STATE changed', {
        oldLength: timelineStateRef.current?.length,
        newLength: timeline?.length,
        isNewReference: timeline !== timelineStateRef.current
      });
      timelineStateRef.current = timeline;
    }
  }, [timeline]);

  // DEBUG: Track showTaskManager changes
  useEffect(() => {
    console.log('🔧 [DEBUG] UnifiedTimelineIntegrated: showTaskManager changed to:', showTaskManager);
  }, [showTaskManager]);

  // Stable callbacks for TaskManagerPanel to prevent re-renders
  const handleCloseTaskManager = useCallback(() => {
    console.log('🔧 [DEBUG] handleCloseTaskManager called');
    setShowTaskManager(false);
  }, []);

  const handleTaskUpdate = useCallback(() => {
    console.log('🔧 [DEBUG] handleTaskUpdate called');
    refetchTimeline();
  }, [refetchTimeline]);

  const [allViewTab, setAllViewTab] = useState('all'); // all, ready, progress, completed, dismissed, overdue
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('due_date');
  const [sortOrder, setSortOrder] = useState('asc');

  // Get global deadlines hook
  const { globalDeadlines } = useGlobalDeadlines();

  // Use session context for session management
 // Use session context instead of local state
 const { startSession, toggleTaskCompletion, activeSession } = useSessionContext();

  // Refs for stable dependencies (prevent unnecessary re-renders)
  const collegesRef = useRef(colleges);
  const hasInitializedDateRef = useRef(hasInitializedDate);
  const batchingEnabledRef = useRef(batchingEnabled);
  const hasEvaluatedCompletionRulesRef = useRef(false);
  const timelineRef = useRef(timeline); // Add timeline ref for stable access

  // Keep refs in sync with their values
  useEffect(() => { collegesRef.current = colleges; }, [colleges]);
  useEffect(() => { hasInitializedDateRef.current = hasInitializedDate; }, [hasInitializedDate]);
  useEffect(() => { batchingEnabledRef.current = batchingEnabled; }, [batchingEnabled]);
  useEffect(() => { timelineRef.current = timeline; }, [timeline]); // Keep timeline ref in sync

  // Sync hook's timeline to local state (Timeline page has local mutations via setTimeline)
  useEffect(() => {
    if (rawTimeline.length > 0) {
      console.log(`[UnifiedTimeline] Syncing ${rawTimeline.length} tasks from hook to local state`);

      // Transform for Timeline page's specific needs (category mapping, status, batching)
      const transformedTasks = rawTimeline.map(item => {
        return {
          ...item,
          category: getCategoryKey(item.category),
          status: mapCompletionStatus(item.completion_status, item.status),
          estimated_hours: item.estimated_duration ? item.estimated_duration / 60 : 1,
          priority: item.priority || (item.is_critical ? 'high' : 'normal'),
          college_journey_link: item.module_link || null,
          original: item // Keep original for updates
        };
      });

      console.log('🔧 [DEBUG] UnifiedTimelineIntegrated: Calling setTimeline with', transformedTasks.length, 'tasks');
      setTimeline(transformedTasks);

      // Apply batching if enabled
      if (batchingEnabledRef.current) {
        const batched = TaskBatchingService.createDynamicBatches(
          transformedTasks,
          new Date(),
          {
            maxBatchSize: 6,
            minBatchSize: 3,
            preferredBatchSize: 4,
            groupBySimilarity: true
          }
        );

        const distributed = TaskBatchingService.distributeSessionsAcrossDays(
          batched,
          new Date(),
          {
            maxTasksPerDay: 4,
            preferWeekdays: true,
            sessionDays: 3
          }
        );

        setBatchedTimeline(distributed);
      } else {
        setBatchedTimeline(transformedTasks);
      }

      // Initialize selected date to today on first load
      if (!hasInitializedDateRef.current) {
        console.log('Initializing selectedDate to today');
        setSelectedDate(new Date());
        setHasInitializedDate(true);
      }
    }
  }, [rawTimeline, batchingEnabled]);

  // Task status types (matching your existing system)
  const TASK_STATUS = {
    NOT_STARTED: 'not_started',
    IN_PROGRESS: 'in_progress',
    BLOCKED: 'blocked',
    COMPLETED: 'completed'
  };

  // Get the hard deadline from college's global_colleges based on application round
  const getHardDeadline = (college) => {
    if (!college?.global_colleges) return null;

    const round = college.application_round;
    const gc = college.global_colleges;

    switch(round) {
      case 'early_decision':
      case 'early_decision_1':
        return gc.ed_deadline;
      case 'early_decision_2':
        return gc.ed2_deadline;
      case 'early_action':
        return gc.ea_deadline;
      case 'regular_decision':
      default:
        return gc.rd_deadline;
    }
  };

  // Load milestones for progress tracking
  useEffect(() => {
    const loadMilestones = async () => {
      if (!currentUser?.id) return;

      const { data: milestoneData } = await supabase
        .from('milestone_templates')
        .select('*')
        .eq('student_id', currentUser.id);

      if (milestoneData) {
        setMilestones(milestoneData);
      }
    };

    loadMilestones();
  }, [currentUser?.id]);

  // Helper functions
  const getCategoryKey = (category) => {
    if (!category) return 'admin';
    // Direct mapping for database categories
    const directMap = {
      'essay': 'essay',
      'application': 'admin',
      'research': 'research',
      'testing': 'testing',
      'profile': 'profile',
      'financial_aid': 'financial',
      'financial': 'financial'
    };
    return directMap[category.toLowerCase()] || 'admin';
  };

  const mapCompletionStatus = (completionStatus, status) => {
    if (completionStatus === 'completed' || status === 'completed') return TASK_STATUS.COMPLETED;
    if (completionStatus === 'in_progress' || status === 'in_progress') return TASK_STATUS.IN_PROGRESS;
    if (status === 'rescheduled' || status === 'blocked') return TASK_STATUS.BLOCKED;  // Map 'rescheduled' to blocked
    if (status === 'cancelled') return TASK_STATUS.BLOCKED;  // Cancelled tasks also show as blocked
    return TASK_STATUS.NOT_STARTED;
  };

  // Check for external completion updates (e.g., from College Journey modules)
  const checkForExternalCompletions = useCallback(async () => {
    if (!currentUser?.id || timeline.length === 0) return;

    try {
      // Get fresh data from the database for incomplete tasks
      const incompleteTaskIds = timeline
        .filter(t => t.status !== TASK_STATUS.COMPLETED)
        .map(t => t.id);

      if (incompleteTaskIds.length === 0) return;

      const { data: updatedTasks, error } = await supabase
        .from('timeline_items')
        .select('id, completion_status, completed, completion_date, title')
        .in('id', incompleteTaskIds)
        .eq('completion_status', 'completed');

      if (error) {
        console.error('Error checking for completions:', error);
        return;
      }

      if (updatedTasks && updatedTasks.length > 0) {
        console.log('Found externally completed tasks:', updatedTasks.length);

        // Update local state with newly completed tasks
        setTimeline(prev => prev.map(task => {
          const updated = updatedTasks.find(u => u.id === task.id);
          if (updated) {
            toast.success(`Task "${updated.title}" completed!`);
            return {
              ...task,
              status: TASK_STATUS.COMPLETED,
              isCompleted: true,
              completion_status: 'completed',
              completion_date: updated.completion_date
            };
          }
          return task;
        }));
      }
    } catch (error) {
      console.error('Error in external completion check:', error);
    }
  }, [currentUser?.id, timeline]);

  // Calculate workload for a given date
  const getDateWorkload = (date) => {
    const dayTasks = timeline.filter(task =>
      task.display_date && isSameDay(parseLocalDate(task.display_date), date)
    );
    const totalHours = dayTasks.reduce((sum, task) => sum + (task.estimated_hours || 0), 0);

    if (totalHours === 0) return 'none';
    if (totalHours < 2) return 'light';
    if (totalHours <= 4) return 'moderate';
    if (totalHours <= 6) return 'heavy';
    return 'overloaded';
  };

  // Get tasks for a specific date range
  const getTasksForDateRange = (startDate, endDate) => {
    return batchedTimeline.filter(item => {
      // Handle batches
      if (item.isBatch) {
        if (!item.scheduled_date) return false;
        const batchDate = parseLocalDate(item.scheduled_date);
        if (isNaN(batchDate.getTime())) return false;
        return batchDate >= startDate && batchDate <= endDate;
      }
      // Handle individual tasks
      if (!item.display_date) return false;
      const taskDate = parseLocalDate(item.display_date);
      // Check if dates are valid before comparison
      if (isNaN(taskDate.getTime())) return false;
      return taskDate >= startDate && taskDate <= endDate;
    });
  };

  // Filter tasks by category or special filters
  const getFilteredTasks = () => {
    // Handle special filters
    if (filterCategory === 'overdue') {
      return batchedTimeline.filter(item => {
        if (item.isBatch) {
          // Check if any task in the batch is overdue
          return item.tasks.some(t => t.isOverdue && !t.isCompleted);
        }
        return item.isOverdue && !item.isCompleted;
      });
    }

    if (filterCategory === 'quick') {
      return batchedTimeline.filter(item => {
        if (item.isBatch) {
          // Check if any task in the batch is quick (30 min or less)
          return item.tasks.some(t => (t.estimated_hours || 0.75) <= 0.5);
        }
        return (item.estimated_hours || 0.75) <= 0.5;
      });
    }

    // Apply category filter
    let filtered = batchedTimeline;
    if (filterCategory !== 'all') {
      filtered = filtered.filter(item => {
        if (item.isBatch) {
          return item.category === filterCategory ||
                 item.tasks.some(t => t.category === filterCategory);
        }
        return item.category === filterCategory;
      });
    }

    // Apply application round filter
    if (filterRound !== 'all') {
      filtered = filtered.filter(item => {
        if (item.isBatch) {
          // Filter batches by their application_round
          return item.application_round === filterRound ||
                 item.tasks.some(t => {
                   const taskRound = t.application_round || t.college_application_round || 'regular';
                   return taskRound === filterRound;
                 });
        }
        // Filter individual tasks by application round
        const taskRound = item.application_round || item.college_application_round || 'regular';
        return taskRound === filterRound;
      });
    }

    return filtered;
  };

  // Toggle task status - wrapped in useCallback to prevent recreation on every render
  const toggleTaskStatus = useCallback(async (taskId) => {
    console.log('🔄 toggleTaskStatus called for task:', taskId);
    // Use ref to access current timeline without adding it as a dependency
    const task = timelineRef.current.find(t => t.id === taskId);
    if (!task) {
      console.error('❌ Task not found in timeline:', taskId);
      return;
    }

    console.log('📊 Current task status:', task.status, 'Full task:', task);

    const statusOrder = [
      TASK_STATUS.NOT_STARTED,
      TASK_STATUS.IN_PROGRESS,
      TASK_STATUS.BLOCKED,
      TASK_STATUS.COMPLETED
    ];

    const currentIndex = statusOrder.indexOf(task.status);
    // If status not found or invalid, start from NOT_STARTED
    const validIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex = (validIndex + 1) % statusOrder.length;
    const newStatus = statusOrder[nextIndex];

    console.log('🔄 Status transition:', task.status, '→', newStatus, `(index ${currentIndex}/${validIndex} → ${nextIndex})`);

    // Update locally first - both timeline and batchedTimeline
    setTimeline(prev => prev.map(t =>
      t.id === taskId ? {
        ...t,
        status: newStatus,
        isCompleted: newStatus === TASK_STATUS.COMPLETED
      } : t
    ));

    // Also update batchedTimeline to ensure UI reflects changes immediately
    setBatchedTimeline(prev => {
      const updated = prev.map(item => {
        if (item.isBatch) {
          // If it's a batch, update the task within the batch
          return {
            ...item,
            tasks: item.tasks.map(t =>
              t.id === taskId ? {
                ...t,
                status: newStatus,
                isCompleted: newStatus === TASK_STATUS.COMPLETED
              } : t
            ),
            completedCount: item.tasks.filter(t =>
              t.id === taskId ? newStatus === TASK_STATUS.COMPLETED : t.isCompleted
            ).length
          };
        } else {
          // If it's an individual task
          return item.id === taskId ? {
            ...item,
            status: newStatus,
            isCompleted: newStatus === TASK_STATUS.COMPLETED
          } : item;
        }
      });
      console.log('✅ Updated batchedTimeline:', updated);
      return updated;
    });

    // Update in database
    try {
      const updates = {
        completion_status: newStatus === TASK_STATUS.COMPLETED ? 'completed' :
                          newStatus === TASK_STATUS.IN_PROGRESS ? 'in_progress' :
                          'not_started',
        status: newStatus === TASK_STATUS.COMPLETED ? 'completed' :
                newStatus === TASK_STATUS.IN_PROGRESS ? 'in_progress' :
                newStatus === TASK_STATUS.BLOCKED ? 'rescheduled' :  // Use 'rescheduled' for blocked status
                'planned',  // Use 'planned' instead of 'active' for not_started
        completed: newStatus === TASK_STATUS.COMPLETED,
        completion_date: newStatus === TASK_STATUS.COMPLETED ? new Date().toISOString() : null
      };

      await timelineService.upsertTimelineItem({
        id: taskId,
        ...updates
      });

      if (newStatus === TASK_STATUS.COMPLETED) {
        toast.success('Task completed!');
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
      // Revert on error
      refetchTimeline();
    }
  }, [refetchTimeline]); // Only refetchTimeline as dependency - timeline accessed via ref

  // Reschedule task or batch
  const rescheduleTask = async (itemOrId, newDate) => {
    // Check if we're dealing with a batch or individual task
    const isBatch = typeof itemOrId === 'object' && itemOrId.isBatch;
    const item = isBatch ? itemOrId : timeline.find(t => t.id === itemOrId);

    if (!item) return;

    // Prevent double-rescheduling
    const itemId = isBatch ? item.id : item.id;
    if (reschedulingTaskId === itemId) return;

    setReschedulingTaskId(itemId);
    setShowRescheduleMenu(null);

    const newDateStr = format(newDate, 'yyyy-MM-dd');

    if (isBatch) {
      // Handle batch rescheduling
      const taskIds = item.tasks.map(t => t.id);

      // Update local timeline state for all tasks in batch
      setTimeline(prev => prev.map(t =>
        taskIds.includes(t.id) ? {
          ...t,
          scheduled_date: newDateStr,
          display_date: newDateStr
        } : t
      ));

      // Update batched timeline
      setBatchedTimeline(prev => prev.map(b => {
        if (b.id === item.id) {
          return {
            ...b,
            scheduled_date: newDateStr,
            display_date: newDateStr,
            tasks: b.tasks.map(t => ({
              ...t,
              scheduled_date: newDateStr,
              display_date: newDateStr
            }))
          };
        }
        return b;
      }));

      // Update in database for each task
      try {
        const updatePromises = taskIds.map(taskId =>
          timelineService.upsertTimelineItem({
            id: taskId,
            scheduled_date: newDateStr
          })
        );

        await Promise.all(updatePromises);
        toast.success(`Batch of ${taskIds.length} tasks rescheduled`);
      } catch (error) {
        console.error('Error rescheduling batch:', error);
        toast.error('Failed to reschedule batch');
        // Revert on error
        refetchTimeline();
      }
    } else {
      // Handle individual task rescheduling (existing logic)
      setTimeline(prev => prev.map(t =>
        t.id === itemId ? {
          ...t,
          scheduled_date: newDateStr,
          display_date: newDateStr
        } : t
      ));

      // Update in database
      try {
        await timelineService.upsertTimelineItem({
          id: itemId,
          scheduled_date: newDateStr
        });
        toast.success('Task rescheduled');
      } catch (error) {
        console.error('Error rescheduling task:', error);
        toast.error('Failed to reschedule task');
        // Revert on error
        refetchTimeline();
      }
    }

    setReschedulingTaskId(null);
  };

  // Calculate progress percentages
  const calculateProgress = () => {
    const filteredTasks = getFilteredTasks();
    const completed = filteredTasks.filter(t => t.status === TASK_STATUS.COMPLETED).length;
    const total = filteredTasks.length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const calculateMilestoneProgress = (categoryKey) => {
    const categoryTasks = timeline.filter(t => t.category === categoryKey);
    const completed = categoryTasks.filter(t => t.status === TASK_STATUS.COMPLETED).length;
    return categoryTasks.length > 0 ? Math.round((completed / categoryTasks.length) * 100) : 0;
  };

  // Get week tasks summary
  const getWeekSummary = () => {
    const weekStart = startOfWeek(selectedDate);
    const weekTasks = getTasksForDateRange(weekStart, addDays(weekStart, 6));
    const completed = weekTasks.filter(t => t.isCompleted).length;
    const activeTasks = weekTasks.filter(t => !t.isCompleted);
    const totalHours = activeTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);

    const tomorrowTasks = getTasksForDateRange(
      addDays(new Date(), 1),
      addDays(new Date(), 1)
    ).filter(t => !t.isCompleted);
    const tomorrowHours = tomorrowTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);

    return {
      total: weekTasks.length,
      completed,
      totalHours,
      remaining: weekTasks.length - completed,
      remainingHours: activeTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0),
      tomorrowCount: tomorrowTasks.length,
      tomorrowHours
    };
  };

  // Status checkbox component
  const StatusCheckbox = ({ task, onClick }) => {
    const statusIcons = {
      [TASK_STATUS.NOT_STARTED]: <Circle className="w-5 h-5 text-stone-400" />,
      [TASK_STATUS.IN_PROGRESS]: <div className="relative">
        <Circle className="w-5 h-5 text-amber-500" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" />
        </div>
      </div>,
      [TASK_STATUS.BLOCKED]: <PauseCircle className="w-5 h-5 text-red-500" />,
      [TASK_STATUS.COMPLETED]: <CheckCircle2 className="w-5 h-5 text-emerald-500" />
    };

    // Get current status (with fallback)
    const currentStatus = task.status || TASK_STATUS.NOT_STARTED;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          console.log('📱 StatusCheckbox clicked for task:', task.id, 'current status:', task.status);
          onClick(task.id);
        }}
        className="flex-shrink-0 p-0.5 hover:bg-stone-50 rounded transition-colors"
      >
        {statusIcons[currentStatus]}
      </button>
    );
  };

  // Task card component
  const TaskCard = ({ task, onDragStart, onDragEnd, onToggleStatus, activeSession }) => {
    const categoryInfo = Object.values(CATEGORIES).find(c => c.key === task.category) || CATEGORIES['Application Execution'];
    const taskType = getTaskType(task);
    const isCompleted = task.isCompleted;
    const isOverdue = task.isOverdue;

    // Check if this task should have a session option
    const shouldShowSession = !isCompleted && (
      task.category === 'essay' || // Essay tasks
      (task.estimated_hours && task.estimated_hours >= 1.5) || // Long tasks
      task.title?.toLowerCase().includes('research') || // Research tasks
      task.title?.toLowerCase().includes('brainstorm') // Brainstorming tasks
    );

    // Check if this task has an active session
    const isTaskSessionActive = activeSession && activeSession.id === `task-${task.id}`;
    const isOtherSessionActive = activeSession && activeSession.id !== `task-${task.id}`;

    return (
      <div
        onContextMenu={(e) => {
          e.preventDefault();
          if (!isCompleted) setShowRescheduleMenu(task.id);
        }}
        className={`
          border rounded-lg p-3 mb-2 transition-all duration-200 group relative
          ${reschedulingTaskId === task.id ? 'bg-stone-50 border-stone-300 opacity-50 pointer-events-none' :
            isTaskSessionActive ? 'bg-teal-50 border-teal-400 ring-2 ring-teal-400 ring-opacity-50' :
            isOtherSessionActive && shouldShowSession ? 'bg-stone-50 border-stone-200 opacity-50' :
            isOverdue ? 'bg-white border-slate-900' :
            isCompleted ? 'bg-stone-50 border-stone-200 opacity-50' :
            'bg-white border-stone-200 hover:shadow-sm'}
        `}
      >
        <div className="flex items-start gap-2">
          {/* Drag Handle - always reserve space to prevent layout shift */}
          <div
            draggable={task.status !== TASK_STATUS.COMPLETED}
            onDragStart={(e) => task.status !== TASK_STATUS.COMPLETED && onDragStart(e, task)}
            onDragEnd={onDragEnd}
            className={`flex-shrink-0 p-1 rounded transition-all will-change-auto ${
              task.status !== TASK_STATUS.COMPLETED
                ? 'hover:bg-stone-100 cursor-move opacity-0 invisible group-hover:opacity-100 group-hover:visible'
                : 'cursor-default invisible'
            }`}
            title={task.status !== TASK_STATUS.COMPLETED ? "Drag to reschedule" : ""}
          >
            <GripVertical className="w-4 h-4 text-stone-400" />
          </div>

          <StatusCheckbox task={task} onClick={onToggleStatus || toggleTaskStatus} />
          {/* red badge */}
          {/* Task Details */}
          <div className="flex-1">
                      <h3 className="text-sm font-medium text-slate-900 mb-1">
                        {task.title || task.task_name || 'Complete Task'}
                     
                      {task.college_journey_link && (
                  <button
                    onClick={() => navigate(task.college_journey_link)}
                    className="p-1.5 ml-2 bg-azure-100 hover:bg-azure-200 text-azure-700 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all will-change-auto"
                    title="Open in College Journey"
                  >
                    <Link2 className="w-3.5 h-3.5" />
                  </button>
                )}
                 </h3>
                      <p className="text-slate-600 text-xs">
                        
                        
              {task.college_name && (
                <p className="text-xs text-slate-800">
                  {task.college_name}
                </p>
              )}
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${categoryInfo.color}`}>
                {categoryInfo.label}
              </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!task.is_hard_deadline) {
                              setShowRescheduleMenu(task.id);
                            } else {
                              toast.error('Cannot reschedule hard deadline');
                            }
                          }}
                          className={`text-xs ${!task.is_hard_deadline ? 'hover:text-azure-600 cursor-pointer underline decoration-dotted' : 'cursor-not-allowed opacity-70'}`}
                          disabled={task.is_hard_deadline}
                        >
                          {isOverdue ?
                            `Was due ${new Date(task.deadline_date || task.due_date).toLocaleDateString()}` :
                            `Due ${new Date(task.deadline_date || task.due_date).toLocaleDateString()}`
                          }
                        </button>
                      </p>
                      {isOverdue && (
                        <p className="text-red-600 font-medium text-xs mt-1">
                          ⚠️ {task.daysOverdue} {task.daysOverdue === 1 ? 'DAY' : 'DAYS'} OVERDUE
                        </p>
                      )}
                      {task.daysLeft === 0 && (
                        <p className="text-amber-600 font-medium text-sm mt-1">
                          ⏰ Due today!
                        </p>
                      )}
                <div className="flex items-center gap-1">
                {/* Session button for eligible tasks */}
                <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                {/* Active session indicator */}
                {isTaskSessionActive && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="relative">
                      <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                      <div className="absolute inset-0 w-2 h-2 bg-teal-400 rounded-full animate-ping"></div>
                    </div>
                    <span className="text-xs text-teal-700 font-medium">Session Active</span>
                  </div>
                )}
               
                
              </div>
              
            
                
              </div>
                    </div>
                    </div>

          
           

            <div className="flex items-center gap-2 mt-1">

              <span className="text-xs text-slate-800">
                ⏱ {formatHours(task.estimated_hours || 0.5)}h
              </span>
              {task.isRescheduled && (
                <span className="text-xs text-amber-600 font-medium" title={`Originally due: ${task.due_date}`}>
                  📅 Rescheduled
                </span>
              )}
              {task.completed_as_mvv && isCompleted && (
                <span className="text-xs text-violet-600 font-medium" title="Quick version complete - enhancement available">
                  ✓ Quick
                </span>
              )}
              {task.priority === 'high' && (
                <span className="w-2 h-2 bg-red-500 rounded-full" />
              )}
            </div>
          

          {/* Action Button */}
          {!isCompleted && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleTaskAction(task, navigate);
              }}
              className="opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all will-change-auto px-3 py-1.5 text-xs font-medium text-white rounded-lg shadow-sm bg-azure-500 hover:bg-azure-600"
              title={taskType.actionText}
            >
              {taskType.actionText}
            </button>
          )}

          {/* Enhancement Button for MVV completions */}
          {isCompleted && task.completed_as_mvv && !task.mvv_upgrade_prompted_at && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                const timeToEnhance = Math.round((task.optimal_time_required - task.mvv_time_required) / 60 * 10) / 10;
                if (confirm(`Enhance this task for a stronger outcome? (~${timeToEnhance}h more)`)) {
                  // Mark as prompted
                  await supabase
                    .from('timeline_items')
                    .update({ mvv_upgrade_prompted_at: new Date().toISOString() })
                    .eq('id', task.id);
                  // Open task for enhancement (navigate or show modal)
                  handleTaskAction(task, navigate);
                  toast.success('Ready to enhance! Use the extra time to strengthen this work.');
                }
              }}
              className="opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all will-change-auto px-3 py-1.5 text-xs font-medium text-white rounded-lg shadow-sm bg-violet-500 hover:bg-violet-600"
              title="Enhance for stronger outcome"
            >
              Enhance (+{Math.round((task.optimal_time_required - task.mvv_time_required) / 60 * 10) / 10}h)
            </button>
          )}

          <button
            className="opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all will-change-auto p-1"
            onClick={(e) => {
              e.stopPropagation();
              setShowRescheduleMenu(task.id);
            }}
          >
            <MoreVertical className="w-4 h-4 text-stone-400" />
          </button>
        </div>

        {/* Reschedule menu */}
        {showRescheduleMenu === task.id && (
          <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg z-50 py-1 min-w-[160px]">
            <button
              onClick={() => rescheduleTask(task.id, new Date())}
              className="w-full px-3 py-2 text-left text-sm font-medium hover:bg-stone-50"
            >
              Move to Today
            </button>
            <button
              onClick={() => rescheduleTask(task.id, addDays(new Date(), 1))}
              className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50"
            >
              Move to Tomorrow
            </button>
            <button
              onClick={() => rescheduleTask(task.id, addDays(new Date(), 7))}
              className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50"
            >
              Move to Next Week
            </button>
            <button
              onClick={() => {
                const nextWeekend = new Date();
                nextWeekend.setDate(nextWeekend.getDate() + (6 - nextWeekend.getDay()));
                rescheduleTask(task.id, nextWeekend);
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50"
            >
              Move to Weekend
            </button>
            <hr className="my-1" />
            <button
              onClick={() => {
                setDatePickerTaskId(task.id);
                setShowDatePicker(true);
                setShowRescheduleMenu(null);
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50 text-azure-600 font-medium"
            >
              📅 Choose Custom Date...
            </button>
            <hr className="my-1" />
            <button
              onClick={() => setShowRescheduleMenu(null)}
              className="w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-stone-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    );
  };

  // Get dynamic title for Focus view based on selected date
  const getFocusTitle = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);

    if (selected.getTime() === today.getTime()) {
      return "Today + Next 3 Days";
    }
    const diff = differenceInDays(selected, today);
    if (diff > 0) {
      return `${format(selected, 'MMM d')} + 3 days`;
    }
    return `4 days from ${format(selected, 'MMM d')}`;
  };

  // Focus view component
  const FocusView = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = [0, 1, 2, 3].map(offset => addDays(selectedDate, offset));
    const filteredTimeline = getFilteredTasks();

    // Memoize overdue tasks to prevent unnecessary recalculations
    const overdueTasks = useMemo(() => {
      return filteredTimeline.filter(task => task.isOverdue);
    }, [filteredTimeline]);

    // Debug logging (disabled to prevent console spam)
    // console.log('Focus View - Filtered timeline:', filteredTimeline);
    // console.log('Focus View - Days to display:', days.map(d => format(d, 'yyyy-MM-dd')));
    // console.log('Focus View - Overdue tasks:', overdueTasks.length);

    return (
      <div className="space-y-6">
        {/* Gentle catch-up mode for overdue tasks - Always reserve space to prevent layout shifts */}
        <div className="transition-all duration-200">
          {overdueTasks.length > 0 && selectedDate >= today ? (
            <div className="bg-white border-2 border-coral-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3 bg-red-500 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                <span className="p-2 bg-white/20 rounded-xl">
              <AlertCircle className="w-6 h-6 text-white" />
            </span>
                  <span className="text-sm font-medium text-white">
                    You have {overdueTasks.length} {overdueTasks.length === 1 ? 'task' : 'tasks'} to catch up on
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {overdueTasks.some(t => t.priority === 'critical') && (
                    <span className="text-xs px-2 py-1 bg-amber-100 text-slate-700 rounded-full">
                      Start with critical items
                    </span>
                  )}
                  {crisisLevel !== 'normal' && (
                    <button
                      onClick={() => setShowCrisisSupport(true)}
                      className="px-3 py-1.5 bg-white text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors flex items-center gap-1.5"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      I'm overwhelmed
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {overdueTasks
                  .sort((a, b) => {
                    // Sort by priority (critical first), then by age
                    if (a.priority === 'critical' && b.priority !== 'critical') return -1;
                    if (b.priority === 'critical' && a.priority !== 'critical') return 1;
                    return b.daysOverdue - a.daysOverdue;
                  })
                  .slice(0, showAllOverdueTasks ? overdueTasks.length : 3)
                  .map(task => (
                    <div key={task.id} className="relative">
                      <OverdueTaskCard
                        task={task}
                        showEstimatedTime={true}
                        onStatusChange={() => {
                          // Update local state to reflect status change
                          refetchTimeline(); // Refresh timeline to show updated status
                        }}
                        onActionClick={(task) => {
                          // Navigate to task details or continue with current workflow
                          if (task.college_journey_link) {
                            navigate(task.college_journey_link);
                          } else {
                            // Default action - toggle task status
                            toggleTaskStatus(task.id);
                          }
                        }}
                        onReschedule={(taskId) => {
                          setShowRescheduleMenu(taskId);
                        }}
                      />

                      {/* Reschedule menu for this specific overdue task */}
                      {showRescheduleMenu === task.id && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg z-50 py-1 min-w-[160px]">
                          <button
                            onClick={() => rescheduleTask(task.id, new Date())}
                            className="w-full px-3 py-2 text-left text-sm font-medium hover:bg-stone-50"
                          >
                            Move to Today
                          </button>
                          <button
                            onClick={() => rescheduleTask(task.id, addDays(new Date(), 1))}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50"
                          >
                            Move to Tomorrow
                          </button>
                          <button
                            onClick={() => rescheduleTask(task.id, addDays(new Date(), 7))}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50"
                          >
                            Move to Next Week
                          </button>
                          <button
                            onClick={() => {
                              const nextWeekend = new Date();
                              nextWeekend.setDate(nextWeekend.getDate() + (6 - nextWeekend.getDay()));
                              rescheduleTask(task.id, nextWeekend);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50"
                          >
                            Move to Weekend
                          </button>
                          <hr className="my-1" />
                          <button
                            onClick={() => {
                              setDatePickerTaskId(task.id);
                              setShowDatePicker(true);
                              setShowRescheduleMenu(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50 text-azure-600 font-medium"
                          >
                            📅 Choose Custom Date...
                          </button>
                          <hr className="my-1" />
                          <button
                            onClick={() => setShowRescheduleMenu(null)}
                            className="w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-stone-50"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
              {overdueTasks.length > 3 && (
                <button 
                onClick={() => setShowAllOverdueTasks(!showAllOverdueTasks)}
                className="mt-2 text-xs text-slate-700 hover:text-slate-800 font-medium flex items-center gap-1"
                >
                  {showAllOverdueTasks ? (
                    <>
                      <ChevronUp className="w-3 h-3" />
                      Show less
                    </>
                  ) : (
                    <>
                    <ChevronDown className="w-3 h-3" />
                    + {overdueTasks.length - 3} more
                    </>
                  )}
                </button>
              )}
            </div>
          ) : (
            // Reserve minimal space when no overdue tasks to prevent layout shifts
            <div className="h-0 overflow-hidden" />
          )}
        </div>

        {days.map((date, index) => {
          const dayTasks = filteredTimeline.filter(task => {
            if (!task.scheduled_date) return false;
            const taskDate = parseLocalDate(task.scheduled_date);
            // Don't show overdue tasks in regular day sections
            if (task.isOverdue && selectedDate >= today) return false;
            const isMatch = isSameDay(taskDate, date);
            // Debug logging disabled to prevent console spam
            // if (isMatch) {
            //   console.log(`Task "${task.title}" matches date ${format(date, 'yyyy-MM-dd')}`);
            // }
            return isMatch;
          });
          const activeTasks = dayTasks.filter(t => !t.isCompleted);
          const totalHours = activeTasks.reduce((sum, task) => sum + (task.estimated_hours || 0), 0);
          
          // Determine if this is an empty day
          const isEmpty = dayTasks.length === 0;
          const isTodayFlag = index === 0 && isToday(date);

          return (
            <div
              key={date.toISOString()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (draggedTask) {
                  // Pass the whole object if it's a batch, otherwise just the ID
                  rescheduleTask(draggedTask.isBatch ? draggedTask : draggedTask.id, date);
                }
              }}
              className={`
                ${isEmpty 
                  ? 'border border-slate-100 rounded-lg bg-slate-25 mb-3' 
                  : 'border border-slate-200 rounded-xl bg-white shadow-sm mb-4'
                }
                ${isTodayFlag ? 'ring-2 ring-azure-200 border-azure-300' : ''}
                ${!isEmpty ? 'p-4' : 'p-3'}
                transition-all duration-200
              `}
            >
              {/* Day Header */}
              <div className={`flex items-baseline justify-between ${isEmpty ? 'mb-1' : 'mb-4'}`}>
                <div className="flex items-center gap-3">
                  <h3 className={`font-semibold ${isEmpty ? 'text-sm text-slate-600' : 'text-base text-slate-900'}`}>
                    {index === 0 && isToday(date) ? 'Today' :
                     index === 1 && isToday(addDays(new Date(), -1)) ? 'Tomorrow' :
                     format(date, 'EEEE')}
                  </h3>
                  <span className={`text-sm ${isEmpty ? 'text-slate-500' : 'text-slate-700'}`}>
                    {format(date, 'MMM d')}
                  </span>
                  {index === 0 && isToday(date) && (
                    <span className="px-2 py-0.5 bg-azure-100 text-azure-700 text-xs font-medium rounded-full">
                      Focus Day
                    </span>
                  )}
                </div>
                <span className={`text-sm ${isEmpty ? 'text-slate-400' : 'text-slate-600 font-medium'}`}>
                  {totalHours > 0 ? `~${formatHours(totalHours)}` : 'No tasks'}
                </span>
              </div>

              {/* Day Content */}
              {!isEmpty ? (
                <div className="space-y-3">
                  {dayTasks.map(item => (
                    item.isBatch ? (
                      <BatchTaskCard
                        key={item.id}
                        batch={item}
                        onToggleExpand={(batchId) => {
                          setBatchedTimeline(prev => prev.map(b =>
                            b.id === batchId ? { ...b, isExpanded: !b.isExpanded } : b
                          ));
                        }}
                        onCompleteAll={async (batchId) => {
                          const batch = batchedTimeline.find(b => b.id === batchId);
                          if (batch) {
                            for (const task of batch.tasks) {
                              if (!task.isCompleted) {
                                await toggleTaskStatus(task.id);
                              }
                            }
                          }
                        }}
                        onCompleteTask={toggleTaskStatus}
                        onStartSession={(batchId) => {
                          const batch = batchedTimeline.find(b => b.id === batchId);
                          if (batch) {
                            startSession({
                              ...batch,
                              estimatedMinutes: batch.estimated_hours * 60
                            });
                            toast.success('Session started! Let\'s make progress! 🚀');
                          }
                        }}
                        onDragStart={(e, batch) => setDraggedTask(batch)}
                        onDragEnd={() => setDraggedTask(null)}
                        reschedulingTaskId={reschedulingTaskId}
                        showRescheduleMenu={showRescheduleMenu}
                        onShowRescheduleMenu={setShowRescheduleMenu}
                        onReschedule={rescheduleTask}
                      />
                    ) : (
                      <TaskCard
                        key={item.id}
                        task={item}
                        onDragStart={(e, task) => setDraggedTask(task)}
                        onDragEnd={() => setDraggedTask(null)}
                        onToggleStatus={toggleTaskStatus}
                        activeSession={activeSession}
                      />
                    )
                  ))}

                  {/* Quick task add - only show for non-overloaded days and within reasonable future range */}
                  {totalHours < 6 && index <= 2 && (
                    <QuickTaskAdd
                      date={date}
                      currentUser={currentUser}
                      colleges={colleges}
                      onTaskAdded={() => refetchTimeline()}
                      existingTasks={dayTasks}
                      currentWorkload={totalHours}
                      position="inline"
                    />
                  )}
                </div>
              ) : (
                /* Empty day - compressed view */
                <div className="text-center py-1">
                  <p className="text-xs text-slate-400">No tasks scheduled</p>
                  {index === 3 && (
                    <p className="text-xs text-slate-300 mt-0.5">Great day to catch up or get ahead!</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Month view component
  const MonthView = () => {
    const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    // Navigation functions
    const goToPreviousMonth = () => {
      setSelectedDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
      setSelectedDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    };

    const goToToday = () => {
      setSelectedDate(new Date());
    };

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(new Date(day));
      day = addDays(day, 1);
    }

    const workloadColors = {
      none: 'bg-stone-50',
      light: 'bg-emerald-50',
      moderate: 'bg-azure-50',
      heavy: 'bg-amber-50',
      overloaded: 'bg-red-50'
    };

    const filteredTimeline = getFilteredTasks();

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={goToPreviousMonth}
              className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors"
              title="Previous month"
            >
              <ChevronLeft className="w-5 h-5 text-slate-800" />
            </button>
            <h2 className="text-xl font-semibold text-slate-900 min-w-[140px] text-center">
              {format(selectedDate, 'MMMM yyyy')}
            </h2>
            <button
              onClick={goToNextMonth}
              className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors"
              title="Next month"
            >
              <ChevronRight className="w-5 h-5 text-slate-800" />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-1 text-sm font-medium text-azure-600 hover:bg-azure-50 rounded-lg transition-colors"
            >
              Today
            </button>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-emerald-50 border border-emerald-200 rounded" />
              Light
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-azure-50 border border-azure-200 rounded" />
              Moderate
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-amber-50 border border-amber-200 rounded" />
              Heavy
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-red-50 border border-red-200 rounded" />
              Overloaded
            </span>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-slate-800 py-2">
              {day}
            </div>
          ))}

          {days.map(day => {
            const workload = getDateWorkload(day);
            const dayTasks = filteredTimeline.filter(task =>
              task.scheduled_date && isSameDay(parseLocalDate(task.scheduled_date), day)
            );
            const isCurrentMonth = day.getMonth() === selectedDate.getMonth();
            const hasDeadline = dayTasks.some(t => t.priority === 'high' || t.isOverdue);

            return (
              <div
                key={day.toISOString()}
                onMouseEnter={() => setHoveredDay(day)}
                onMouseLeave={() => setHoveredDay(null)}
                onClick={() => {
                  setSelectedDate(day);
                  setCurrentView('focus');
                }}
                className={`
                  relative aspect-square p-2 rounded-lg cursor-pointer
                  transition-all duration-200 hover:scale-105 hover:shadow-md
                  ${workloadColors[workload]}
                  ${isToday(day) ? 'ring-2 ring-azure-500' : ''}
                  ${!isCurrentMonth ? 'opacity-40' : ''}
                  ${hoveredDay && isSameDay(hoveredDay, day) ? 'z-50' : 'z-0'}
                `}
              >
                <div className="text-sm font-medium text-slate-900">
                  {format(day, 'd')}
                </div>
                {dayTasks.length > 0 && (
                  <div className="text-xs text-slate-800">
                    {formatHours(dayTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0))}h
                  </div>
                )}
                {hasDeadline && (
                  <div className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}

                {/* Hover tooltip */}
                {hoveredDay && isSameDay(hoveredDay, day) && dayTasks.length > 0 && (
                  <div className="absolute z-50 top-full left-1/2 transform -translate-x-1/2 mt-2 p-3 bg-slate-900 text-white rounded-lg shadow-lg min-w-[200px]">
                    <div className="text-xs font-medium mb-2">
                      {format(day, 'EEEE, MMM d')}
                    </div>
                    <div className="space-y-1">
                      {dayTasks.slice(0, 3).map(task => (
                        <div key={task.id} className="text-xs">
                          • {task.title}
                        </div>
                      ))}
                      {dayTasks.length > 3 && (
                        <div className="text-xs text-stone-400">
                          +{dayTasks.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Week view component
  const WeekView = () => {
    const weekStart = startOfWeek(selectedDate);
    const days = [0, 1, 2, 3, 4, 5, 6].map(offset => addDays(weekStart, offset));
    const filteredTimeline = getFilteredTasks();

    return (
      <div className="space-y-6">
        {days.map(date => {
          const dayTasks = filteredTimeline.filter(task =>
            task.display_date && isSameDay(parseLocalDate(task.display_date), date)
          );
          const totalHours = dayTasks.reduce((sum, task) => sum + (task.estimated_hours || 0), 0);
          
          // Determine if this is an empty day
          const isEmpty = dayTasks.length === 0;
          const isTodayFlag = isToday(date);

          return (
            <div
              key={date.toISOString()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (draggedTask) {
                  // Pass the whole object if it's a batch, otherwise just the ID
                  rescheduleTask(draggedTask.isBatch ? draggedTask : draggedTask.id, date);
                }
              }}
              className={`
                ${isEmpty 
                  ? 'border border-slate-100 rounded-lg bg-slate-25 mb-3' 
                  : 'border border-slate-200 rounded-xl bg-white shadow-sm mb-4'
                }
                ${isTodayFlag ? 'ring-2 ring-azure-200 border-azure-300' : ''}
                ${!isEmpty ? 'p-4' : 'p-3'}
                transition-all duration-200
              `}
            >
              {/* Day Header */}
              <div className={`flex items-baseline justify-between ${isEmpty ? 'mb-1' : 'mb-4'}`}>
                <div className="flex items-center gap-3">
                  <h3 className={`font-semibold ${isEmpty ? 'text-sm text-slate-600' : 'text-base text-slate-900'}`}>
                    {format(date, 'EEEE')}
                  </h3>
                  <span className={`text-sm ${isEmpty ? 'text-slate-500' : 'text-slate-700'}`}>
                    {format(date, 'MMM d')}
                  </span>
                  {isToday(date) && (
                    <span className="px-2 py-0.5 bg-azure-100 text-azure-700 text-xs font-medium rounded-full">
                      Today
                    </span>
                  )}
                </div>
                <span className={`text-sm ${isEmpty ? 'text-slate-400' : 'text-slate-600 font-medium'}`}>
                  {totalHours > 0 ? formatHours(totalHours) : 'Free'}
                </span>
              </div>

              {/* Day Content */}
              {!isEmpty ? (
                <div className="space-y-3">
                  {dayTasks.map(item => (
                    item.isBatch ? (
                      <BatchTaskCard
                        key={item.id}
                        batch={item}
                        onToggleExpand={(batchId) => {
                          setBatchedTimeline(prev => prev.map(b =>
                            b.id === batchId ? { ...b, isExpanded: !b.isExpanded } : b
                          ));
                        }}
                        onCompleteAll={async (batchId) => {
                          const batch = batchedTimeline.find(b => b.id === batchId);
                          if (batch) {
                            for (const task of batch.tasks) {
                              if (!task.isCompleted) {
                                await toggleTaskStatus(task.id);
                              }
                            }
                          }
                        }}
                        onCompleteTask={toggleTaskStatus}
                        onStartSession={(batchId) => {
                          const batch = batchedTimeline.find(b => b.id === batchId);
                          if (batch) {
                            startSession({
                              ...batch,
                              estimatedMinutes: batch.estimated_hours * 60
                            });
                            toast.success('Session started! Let\'s make progress! 🚀');
                          }
                        }}
                        onDragStart={(e, batch) => setDraggedTask(batch)}
                        onDragEnd={() => setDraggedTask(null)}
                        reschedulingTaskId={reschedulingTaskId}
                        showRescheduleMenu={showRescheduleMenu}
                        onShowRescheduleMenu={setShowRescheduleMenu}
                        onReschedule={rescheduleTask}
                      />
                    ) : (
                      <TaskCard
                        key={item.id}
                        task={item}
                        onDragStart={(e, task) => setDraggedTask(task)}
                        onDragEnd={() => setDraggedTask(null)}
                        onToggleStatus={toggleTaskStatus}
                        activeSession={activeSession}
                      />
                    )
                  ))}
                </div>
              ) : (
                /* Empty day - compressed view */
                <div className="text-center py-1">
                  <p className="text-xs text-slate-400">No tasks scheduled</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };


  // Category modal component
  const CategoryModal = ({ category, onClose }) => {
    const scrollRef = useRef(null);

    // Memoize categoryTasks to prevent scroll reset on timeline updates
    const categoryTasks = useMemo(() => {
      return timeline.filter(t => t.category === category);
    }, [timeline, category]);

    const categoryInfo = Object.values(CATEGORIES).find(c => c.key === category);

    // Preserve scroll position when categoryTasks changes
    useEffect(() => {
      if (scrollRef.current) {
        const scrollElement = scrollRef.current;
        const currentScrollTop = scrollElement.scrollTop;

        // Restore scroll position after render
        requestAnimationFrame(() => {
          if (scrollElement.scrollTop !== currentScrollTop) {
            scrollElement.scrollTop = currentScrollTop;
          }
        });
      }
    }, [categoryTasks]);

    if (!categoryInfo) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
          <div className="p-6 border-b border-stone-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{categoryInfo.icon}</span>
                <h2 className="text-xl font-semibold text-slate-900">
                  {categoryInfo.label}
                </h2>
                <span className="text-sm text-slate-800">
                  {categoryTasks.length} tasks • {formatHours(categoryTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0))}h total
                </span>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-800" />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="p-6 overflow-y-auto max-h-[60vh]"
          >
            <div className="space-y-2">
              {categoryTasks.map(task => (
                <TaskCard
                  key={`modal-task-${task.id}`}
                  task={task}
                  onDragStart={() => {}}
                  onDragEnd={() => {}}
                  onToggleStatus={toggleTaskStatus}
                  activeSession={activeSession}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };


  // Check for crisis when timeline changes
  useEffect(() => {
    const checkForCrisis = async () => {
      if (!currentUser || !timeline.length) return;

      // TEST MODE CONFIGURATION
      // Set to false to enable crisis features for ALL users (for testing)
      const ENABLE_TEST_MODE_RESTRICTION = false;  // <-- CHANGE THIS TO true TO RESTRICT TO SPECIFIC USERS

      const CRISIS_TEST_USER_IDS = [
        // Add specific user IDs here for testing
        // Example: 'user_123', 'test_student_1'
      ];

      const CRISIS_TEST_NAMES = [
        // Add specific names/emails here for testing (case-insensitive)
        'charlie',  // Enable for Charlie
        'charlie wilkerson'
      ];

      // Debug: Log current user info to understand what fields are available
      console.log('[Crisis Detection] Current user info:', {
        id: currentUser.id,
        email: currentUser.email,
        name: currentUser.name,
        type: currentUser.type,
        isParent: currentUser.isParent
      });

      // Check if current user matches any test criteria (case-insensitive)
      const userIdentifiers = [
        currentUser.email?.toLowerCase(),
        currentUser.name?.toLowerCase(),
        currentUser.display_name?.toLowerCase(),
        currentUser.username?.toLowerCase()
      ].filter(Boolean);

      const isTestUser = !ENABLE_TEST_MODE_RESTRICTION ||
                        CRISIS_TEST_USER_IDS.includes(currentUser.id) ||
                        CRISIS_TEST_NAMES.some(testName =>
                          userIdentifiers.some(identifier =>
                            identifier.includes(testName.toLowerCase())
                          )
                        );

      // If test mode is enabled and user is not a test user, don't show crisis features
      if (ENABLE_TEST_MODE_RESTRICTION && !isTestUser) {
        setCrisisLevel('normal');
        setShowCrisisSupport(false);
        console.log('[Crisis Detection] User not in test mode, crisis features disabled');
        return;
      }

      console.log('[Crisis Detection] Running crisis check...', {
        timelineLength: timeline.length,
        hasCurrentUser: !!currentUser
      });

      const overdueTasks = timeline.filter(task => task.isOverdue && !task.isCompleted);
      const criticalOverdue = overdueTasks.filter(t => t.priority === 'critical');

      console.log('[Crisis Detection] Overdue analysis:', {
        totalOverdue: overdueTasks.length,
        criticalOverdue: criticalOverdue.length,
        sampleTasks: overdueTasks.slice(0, 3).map(t => ({
          title: t.title,
          isOverdue: t.isOverdue,
          isCompleted: t.isCompleted,
          status: t.status
        }))
      });

      // Check if a recovery plan was recently executed (within 10 minutes)
      const lastExecutionTime = sessionStorage.getItem('crisis_plan_executed');
      const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
      if (lastExecutionTime && parseInt(lastExecutionTime) > tenMinutesAgo) {
        console.log('[Crisis Detection] Recovery plan recently executed, skipping detection for 10 minutes');
        return;
      }

      console.log('[Crisis Detection] TEST MODE - Checking crisis status:', {
        overdueCount: overdueTasks.length,
        criticalOverdueCount: criticalOverdue.length,
        userId: currentUser.id,
        userEmail: currentUser.email,
        isTestUser: true
      });

      // Lower thresholds for testing - can trigger with fewer overdue tasks
      const TEST_CRISIS_THRESHOLD = 5;  // Lower for testing (normally 10)
      const TEST_HIGH_STRESS_THRESHOLD = 2;  // Lower for testing (normally 5)

      // Determine crisis level with test thresholds
      if (overdueTasks.length >= TEST_CRISIS_THRESHOLD || criticalOverdue.length >= 2) {
        setCrisisLevel('crisis');
        console.log('[Crisis Detection] TEST MODE - User is in CRISIS');

        // Don't auto-show - let user click the button
        // This was causing the popup to appear immediately without user action
      } else if (overdueTasks.length >= TEST_HIGH_STRESS_THRESHOLD || criticalOverdue.length >= 1) {
        setCrisisLevel('high_stress');
        console.log('[Crisis Detection] TEST MODE - User is in HIGH STRESS');
      } else {
        setCrisisLevel('normal');
      }
    };

    checkForCrisis();
  }, [timeline, currentUser]);

  // Set up real-time subscription for completion status changes
  useEffect(() => {
    if (!currentUser?.id) return;

    // Subscribe to changes in timeline_items table
    const subscription = supabase
      .channel('timeline-completion-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'timeline_items',
          filter: `user_id=eq.${currentUser.id}`
        },
        (payload) => {
          // Check if completion status changed
          if (payload.new.completion_status !== payload.old.completion_status) {
            console.log('Task completion status changed:', payload.new.id, payload.new.completion_status);

            // Update the task in our local timeline
            setTimeline(prev => prev.map(task => {
              if (task.id === payload.new.id) {
                const isCompleted = payload.new.completion_status === 'completed';
                return {
                  ...task,
                  status: isCompleted ? TASK_STATUS.COMPLETED :
                         payload.new.completion_status === 'in_progress' ? TASK_STATUS.IN_PROGRESS :
                         TASK_STATUS.NOT_STARTED,
                  isCompleted,
                  completion_status: payload.new.completion_status,
                  completion_date: payload.new.completion_date
                };
              }
              return task;
            }));

            // Show toast notification for completions
            if (payload.new.completion_status === 'completed' &&
                payload.old.completion_status !== 'completed') {
              toast.success(`Task "${payload.new.title}" marked as complete!`);
            }

            // TODO: Re-enable completion rule evaluation after task status changes
            // Currently disabled to prevent infinite loops
            // setTimeout(async () => {
            //   try {
            //     console.log('🔄 Triggering completion rule evaluation after task status change...');
            //     const completionResults = await CompletionRuleEvaluator.evaluateUserTasks(currentUser.id);
            //     if (completionResults.completed > 0 || completionResults.incompleted > 0) {
            //       console.log('✅ Task status change triggered completion evaluation:', completionResults);
            //     }
            //   } catch (evalError) {
            //     console.error('Error evaluating completion rules after task change:', evalError);
            //   }
            // }, 500);
          }
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [currentUser?.id]);

  // Periodically check for external completions (every 30 seconds when component is active)
  useEffect(() => {
    const interval = setInterval(() => {
      checkForExternalCompletions();
    }, 30000); // Check every 30 seconds

    // Also check immediately when timeline changes
    checkForExternalCompletions();

    return () => clearInterval(interval);
  }, [checkForExternalCompletions]);

  // Combine deadlines from multiple sources
  const upcomingDeadlines = useMemo(() => {
    const allDeadlines = [];  
    
    // Add global deadlines
    if (globalDeadlines?.length > 0) {
      globalDeadlines.forEach(deadline => {
        allDeadlines.push({
          ...deadline,
          deadline_date: deadline.due_date, // Map due_date to deadline_date for consistency
          college_id: deadline.college_id || null,
          source: 'global'
        });
      });
    }

    // Add timeline items (from timeline_items table)
    if (timeline?.length > 0) {
      timeline.forEach(item => {
        // Only include incomplete items with due dates
        if (!item.completed && (item.due_date || item.deadline_date)) {
          allDeadlines.push({
            ...item,
            deadline_date: item.deadline_date || item.due_date,
            due_date: item.due_date || item.deadline_date,
            title: item.title || item.task_name || item.description,
            source: 'timeline'
          });
        }
      });
    }

    // Remove duplicates based on title and due date
    const uniqueDeadlines = allDeadlines.reduce((acc, current) => {
      const duplicate = acc.find(item =>
        item.title === current.title &&
        item.deadline_date === current.deadline_date
      );
      if (!duplicate) {
        acc.push(current);
      }
      return acc;
    }, []);

    console.log('Combined deadlines from all sources:', uniqueDeadlines);
    return uniqueDeadlines;
  }, [globalDeadlines, timeline]);

  // Calculate contextual intelligence for workload assessment
  const contextualIntelligence = useMemo(() => {
    const totalHours = timeline.reduce((sum, task) => sum + (task.estimated_hours || 0), 0);
    return {
      capacity: totalHours > 30 ? 'heavy' : totalHours > 20 ? 'moderate' : 'light'
    };
  }, [timeline]);

  // Handle insight actions
  const handleInsightAction = async (insight) => {
    switch (insight.action?.type) {
      case 'preview':
        // Show week view for workload redistribution
        setCurrentView('week');
        setSelectedDate(insight.action.data.targetWeek);
        break;

      case 'suggest_tasks':
        // Navigate to the light week
        setCurrentView('focus');
        setSelectedDate(insight.action.data.targetWeek);
        break;

      case 'filter_category':
        // Filter to specific category
        setFilterCategory(insight.action.data.category);
        break;

      case 'preview_batch':
        // Switch to category view and highlight batching opportunity
        setFilterCategory(insight.action.data.category);
        break;

      case 'focus_prerequisite':
        // Filter to prerequisite category
        setFilterCategory(insight.action.data.prerequisite);
        break;

      default:
        console.log('Insight action applied:', insight.title);
    }

    toast.success('Insight applied! Check your timeline.');
  };

  // Handle smart insight actions with the new service
  const handleSmartInsightAction = async (insight, action) => {
    console.log('Handling smart insight action:', action.action, insight);

    // Use the enhanced SmartInsightsService execution
    const result = await SmartInsightsService.executeInsightAction(
      insight,
      action,
      timeline,
      {
        startSession,
        setFilter: setFilterCategory,
        focusTask: (task) => {
          // Focus on a specific task
          setSelectedDate(parseISO(task.scheduled_date));
          setCurrentView('focus');
          toast.success(`Focusing on: ${task.title}`);
        }
      }
    );

    if (result.success) {
      toast.success(result.message);

      // Handle specific result types
      if (result.recoveryPlan) {
        setShowRecoveryPlan(result.recoveryPlan);
        return;
      }

      if (result.requiresReload) {
        refetchTimeline();
      }

      if (result.navigateTo) {
        setSelectedDate(result.navigateTo.date);
        setCurrentView(result.navigateTo.view);
      }

      if (result.sessionStarted) {
        // Session was started, UI will update automatically
      }
    } else {
      toast.error(result.message || 'Action failed');
    }

    // Keep fallback for actions not yet migrated
    switch (action.action) {
      case 'filter_overdue':
        setFilterCategory('overdue');
        break;

      case 'redistribute_to_week':
        // Move to the target week for redistribution
        if (action.data?.targetWeek) {
          setSelectedDate(action.data.targetWeek);
          setCurrentView('week');
          toast.success('Navigate to target week for task redistribution');
        }
        break;

      case 'schedule_deep_work':
        // Schedule deep work session in light week
        if (action.data?.week) {
          setSelectedDate(action.data.week.weekStart);
          setCurrentView('focus');
          toast.success('Perfect time for deep work!');
        }
        break;

      case 'start_quick_wins':
        // Start a quick wins session with selected tasks
        if (action.data?.tasks && action.data.tasks.length > 0) {
          const sessionData = {
            title: 'Quick Wins Session',
            tasks: action.data.tasks,
            estimatedMinutes: action.data.tasks.reduce((sum, t) =>
              sum + (t.estimated_hours || 0.25) * 60, 0
            )
          };
          startSession(sessionData);
          toast.success('Quick wins session started! Let\'s build momentum! 🚀');
        }
        break;

      case 'focus_prerequisites':
        // Focus on prerequisite tasks
        if (action.data?.blockingTask) {
          toast.success(`Focus on: ${action.data.blockingTask.title}`);
        }
        break;

      case 'create_batch':
        // Create a batch from similar tasks
        if (action.data?.tasks && action.data.tasks.length > 0) {
          toast.success(`Batched ${action.data.tasks.length} ${action.data.type} tasks`);
          // Refresh timeline to show batched tasks
          refetchTimeline();
        }
        break;

      case 'preview_batch':
        setShowInsightPreview(insight);
        break;

      case 'filter_category':
        if (action.data?.category) {
          setFilterCategory(action.data.category);
        }
        break;

      case 'schedule_category_session':
        if (action.data?.tasks && action.data.category) {
          const sessionData = {
            title: `${action.data.category} Focus Session`,
            tasks: action.data.tasks,
            estimatedMinutes: action.data.tasks.reduce((sum, t) =>
              sum + (t.estimated_hours || 0.75) * 60, 0
            )
          };
          startSession(sessionData);
          toast.success(`${action.data.category} session started!`);
        }
        break;

      case 'schedule_weekend_prep':
        toast.success('Weekend prep session scheduled - great planning!');
        break;

      case 'view_next_week':
        setSelectedDate(addDays(startOfWeek(new Date()), 7));
        setCurrentView('week');
        break;

      case 'redistribute_tasks':
        if (action.data?.fromWeek && action.data?.toWeek && action.data?.movableTasks) {
          toast.success(`Moving ${action.data.movableTasks.length} tasks to lighter week`);
        }
        break;

      case 'preview_redistribution':
        setShowInsightPreview(insight);
        break;

      case 'rebalance_cognitive':
        if (action.data?.tasks) {
          toast.success('Let\'s spread out these high-intensity tasks');
        }
        break;

      case 'schedule_prep_session':
        toast.success('Prep session scheduled!');
        break;

      case 'move_to_this_week':
        if (action.data?.targetWeek) {
          setSelectedDate(action.data.targetWeek);
          setCurrentView('week');
          toast.success('Ready to reschedule tasks to this week');
        }
        break;

      default:
        console.log('Unhandled smart insight action:', action.action);
        setShowInsightPreview(insight);
    }
  };

  const handleDismissInsight = (insightId) => {
    smartActionsIntelligence.dismissSuggestion(insightId);
    setSmartInsights(prev => prev.filter(insight => insight.id !== insightId));
    toast.success('Got it - won\'t show this suggestion again');
  };

  if (timelineLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading timeline...</p>
        </div>
      </div>
    );
  }

  const weekSummary = getWeekSummary();

  // Week at a Glance Component - Shows weekly calendar and progress
const WeekAtGlance = ({ timeline = [], contextualIntelligence }) => {
  const today = new Date();
  const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const currentDay = today.getDay() === 0 ? 6 : today.getDay() - 1; // Convert Sunday=0 to Monday=0

  // Get this week's dates
  const getWeekDates = () => {
    const dates = [];
    const monday = new Date(today);
    const daysSinceMonday = currentDay;
    monday.setDate(today.getDate() - daysSinceMonday);

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(date.getDate());
    }
    return dates;
  };

  const weekDates = getWeekDates();

  // Calculate tasks for this week (include completed to count them properly)
  const thisWeekTasks = useMemo(() => {
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - currentDay);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return timeline?.filter(task => {
      const taskDate = new Date(task.scheduled_date || task.due_date || task.deadline_date);
      return taskDate >= startOfWeek && taskDate <= endOfWeek;
    }) || [];
  }, [timeline, today, currentDay]);

  const completedTasks = thisWeekTasks.filter(t => t.isCompleted || t.completed).length;
  const totalTasks = thisWeekTasks.length;
  const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  // Calculate remaining tasks and actual hours
  const remainingTasks = thisWeekTasks.filter(t => !t.isCompleted && !t.completed);
  const remainingHours = remainingTasks.reduce((sum, task) => {
    return sum + (task.estimated_hours || 1.5);
  }, 0);

  // Calculate tomorrow's tasks and hours
  const tomorrowTasks = useMemo(() => {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const dayAfter = new Date(tomorrow);
    dayAfter.setDate(dayAfter.getDate() + 1);

    return timeline?.filter(task => {
      if (task.isCompleted || task.completed) return false;
      const taskDate = new Date(task.scheduled_date || task.due_date || task.deadline_date);
      return taskDate >= tomorrow && taskDate < dayAfter;
    }) || [];
  }, [timeline, today]);

  const tomorrowHours = tomorrowTasks.reduce((sum, task) => {
    return sum + (task.estimated_hours || 1.5);
  }, 0);

  // Calculate today's tasks and hours for display
  const todayTasks = useMemo(() => {
    const todayStart = new Date(today);
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);

    return timeline?.filter(task => {
      if (task.isCompleted || task.completed) return false;
      const taskDate = new Date(task.scheduled_date || task.due_date || task.deadline_date);
      return taskDate >= todayStart && taskDate <= todayEnd;
    }) || [];
  }, [timeline, today]);

  const todayHours = todayTasks.reduce((sum, task) => {
    return sum + (task.estimated_hours || 1.5);
  }, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-900">
              <h3 className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-3">
                Week at a Glance
              </h3>
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-6">
        {weekDays.map((day, idx) => (
          <div key={idx} className="flex flex-col items-center">
            <div className="text-xs text-slate-500 mb-1 font-medium">{day}</div>
            <div className={`
              w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold transition-all
              ${idx === currentDay
                ? 'bg-teal-600 text-white ring-2 ring-teal-400 ring-offset-1 shadow-md'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}
            `}>
              {weekDates[idx]}
            </div>
            {idx === currentDay && todayHours > 0 && (
              <div className="text-xs text-teal-600 mt-1 font-semibold">{todayHours.toFixed(1)}h</div>
            )}
          </div>
        ))}
      </div>

      {/* Progress Circle */}
      <div className="flex justify-center mb-6">
        <div className="relative">
          <svg className="w-32 h-32 transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#e2e8f0"
              strokeWidth="12"
              fill="none"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#14b8a6"
              strokeWidth="12"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 56}`}
              strokeDashoffset={`${2 * Math.PI * 56 * (1 - progressPercentage / 100)}`}
              className="transition-all duration-500"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold text-slate-900">{completedTasks}/{totalTasks}</div>
            <div className="text-xs text-slate-600">tasks done</div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-600">Remaining:</span>
          <span className="text-sm font-semibold text-slate-900">
            {remainingTasks.length} tasks • ~{remainingHours.toFixed(1)}h
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-600">Tomorrow:</span>
          <span className="text-sm font-semibold text-slate-900">
            {tomorrowTasks.length} tasks • ~{tomorrowHours.toFixed(1)}h
          </span>
        </div>
      </div>

      {/* Workload Alert */}
      {contextualIntelligence?.capacity === 'heavy' || remainingHours > 30 ? (
        <div className="mt-4 bg-amber-50 border border-amber-400 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Heavy Week</p>
              <p className="text-xs text-amber-700">Consider rescheduling non-critical tasks</p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};





  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-slate-800 sticky top-0 z-40 rounded-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            {/* Top row */}
            <div className="flex items-center justify-between mb-4">



            <div className="flex items-center gap-5">
              <button
                onClick={() => navigate('/college')}
                className="p-3 bg-slate-600 hover:bg-slate-700 rounded-xl transition-all duration-300 hover:shadow-sm hover:-translate-y-0.5"
              >
                <ArrowLeft className="w-4 h-4 text-stone-200" />
              </button>
              <div>
              <h1 className="text-2xl font-semibold text-stone-200">
               Timeline
              </h1>
              <p className="text-base opacity-80 font-normal text-stone-100">
              {currentUser?.name || 'Student'} • {colleges?.length || 0} schools
              </p>              </div>
            </div>




              {/* Controls section */}
              <div className="flex items-center gap-3">
                {/* Add Task Button */}
                <button
                  onClick={() => setShowTaskCreator(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-azure-600 hover:bg-azure-700 text-white text-sm font-medium rounded-lg transition-all hover:shadow-sm"
                  title="Add your own college task"
                >
                  <Plus className="w-4 h-4" />
                  Add Task
                </button>
              </div>
            </div>

            {/* Status bar */}
            <div className="flex items-center gap-6 p-3 bg-slate-500 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-100">This Week</span>
                <span className="text-lg font-semibold text-stone-100">
                  {formatHours(weekSummary.totalHours)}
                </span>
              </div>

              <div className="flex-1 flex items-center gap-3">
                <span className="text-xs text-stone-100">Workload</span>
                <div className="flex-1 h-2 bg-white rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all"
                    style={{
                      width: `${Math.min(100, (weekSummary.totalHours / 40) * 100)}%`,
                      background: weekSummary.totalHours > 30 ?
                        'linear-gradient(to right, #ef4444, #f87171)' :
                        weekSummary.totalHours > 20 ?
                        'linear-gradient(to right, #f59e0b, #fbbf24)' :
                        'linear-gradient(to right, #10b981, #34d399)'
                    }}
                  />
                </div>
                <span className={`text-xs font-medium ${
                  weekSummary.totalHours > 30 ? 'text-red-400' :
                  weekSummary.totalHours > 20 ? 'text-gold-300' :
                  'text-teal-200'
                }`}>
                  {weekSummary.totalHours > 30 ? 'Heavy' :
                   weekSummary.totalHours > 20 ? 'Moderate' :
                   'Balanced'}
                </span>
              </div>

              {weekSummary.remaining > 0 && (
                <div className="flex items-center gap-2 px-3 py-1 bg-orange-100 text-slate-800 rounded-full">
                  <span className="text-sm">💪</span>
                  <span className="text-xs font-medium">
                    {weekSummary.remaining} items to complete
                  </span>
                </div>
              )}

              {/* Gentle overdue indicator */}
              {timeline.filter(t => t.isOverdue).length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1 bg-coral-100 text-red-800 rounded-full">
                  <span className="text-xs">📌</span>
                  <span className="text-xs font-medium">
                    {timeline.filter(t => t.isOverdue).length} to catch up
                  </span>
                </div>
              )}

              {/* Active Filter Indicator */}
              {filterCategory !== 'all' && (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                     style={{
                       backgroundColor: filterCategory === 'overdue' ? '#fee2e2' :
                                       filterCategory === 'quick' ? '#dcfce7' :
                                       '#e9d5ff',
                       color: filterCategory === 'overdue' ? '#991b1b' :
                             filterCategory === 'quick' ? '#166534' :
                             '#6b21a8'
                     }}>
                  {filterCategory === 'overdue' && <AlertTriangle className="w-3 h-3" />}
                  {filterCategory === 'quick' && <Zap className="w-3 h-3" />}
                  <span>
                    {filterCategory === 'overdue' ? 'Overdue' :
                     filterCategory === 'quick' ? 'Quick Wins' :
                     CATEGORIES[Object.keys(CATEGORIES).find(k => CATEGORIES[k].key === filterCategory)]?.label || filterCategory}
                  </span>
                  <button
                    onClick={() => setFilterCategory('all')}
                    className="ml-1 hover:opacity-70"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Active Round Filter Indicator */}
              {filterRound !== 'all' && (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
                     style={{
                       backgroundColor: filterRound === 'early_decision' ? '#fae8ff' :
                                       filterRound === 'early_action' ? '#e0e7ff' :
                                       '#f1f5f9',
                       color: filterRound === 'early_decision' ? '#7e22ce' :
                             filterRound === 'early_action' ? '#4338ca' :
                             '#475569'
                     }}>
                  {filterRound === 'early_decision' && <Zap className="w-3 h-3" />}
                  {filterRound === 'early_action' && <Clock className="w-3 h-3" />}
                  {filterRound === 'regular' && <Calendar className="w-3 h-3" />}
                  <span>
                    {filterRound === 'early_decision' ? 'ED Only' :
                     filterRound === 'early_action' ? 'EA Only' :
                     filterRound === 'regular' ? 'RD Only' : filterRound}
                  </span>
                  <button
                    onClick={() => setFilterRound('all')}
                    className="ml-1 hover:opacity-70"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Filter button */}
              <div className="relative">
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="p-2 hover:bg-white rounded-lg transition-colors"
                >
                  <Filter className="w-4 h-4 text-stone-100" />
                  {(filterCategory !== 'all' || filterRound !== 'all') && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-violet-500 rounded-full" />
                  )}
                </button>

                {showFilterDropdown && (
                  <div className="absolute right-0 top-full mt-2 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[180px] z-50">
                    <button
                      onClick={() => {
                        setFilterCategory('all');
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-stone-50 ${
                        filterCategory === 'all' ? 'bg-violet-50 text-violet-700' : ''
                      }`}
                    >
                      All Tasks
                    </button>

                    {/* Special filters */}
                    <div className="border-t border-stone-100 my-1"></div>
                    <button
                      onClick={() => {
                        setFilterCategory('overdue');
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2 ${
                        filterCategory === 'overdue' ? 'bg-red-50 text-red-700' : ''
                      }`}
                    >
                      <AlertTriangle className="w-4 h-4" />
                      <span>Overdue Tasks</span>
                    </button>
                    <button
                      onClick={() => {
                        setFilterCategory('quick');
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2 ${
                        filterCategory === 'quick' ? 'bg-emerald-50 text-emerald-700' : ''
                      }`}
                    >
                      <Zap className="w-4 h-4" />
                      <span>Quick Wins (≤30 min)</span>
                    </button>

                    {/* Category filters */}
                    <div className="border-t border-stone-100 my-1"></div>
                    {Object.values(CATEGORIES).map(cat => (
                      <button
                        key={cat.key}
                        onClick={() => {
                          setFilterCategory(cat.key);
                          setShowFilterDropdown(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2 ${
                          filterCategory === cat.key ? 'bg-violet-50 text-violet-700' : ''
                        }`}
                      >
                        <span>{cat.icon}</span>
                        <span>{cat.label}</span>
                      </button>
                    ))}

                    {/* Application Round filters */}
                    <div className="border-t border-stone-100 my-1"></div>
                    <div className="px-4 py-1 text-xs font-semibold text-stone-500 uppercase tracking-wide">
                      Application Round
                    </div>
                    <button
                      onClick={() => {
                        setFilterRound('all');
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-stone-50 ${
                        filterRound === 'all' ? 'bg-purple-50 text-purple-700' : ''
                      }`}
                    >
                      All Rounds
                    </button>
                    <button
                      onClick={() => {
                        setFilterRound('early_decision');
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2 ${
                        filterRound === 'early_decision' ? 'bg-purple-50 text-purple-700 font-semibold' : ''
                      }`}
                    >
                      <Zap className="w-3 h-3" />
                      <span>Early Decision (ED)</span>
                    </button>
                    <button
                      onClick={() => {
                        setFilterRound('early_action');
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2 ${
                        filterRound === 'early_action' ? 'bg-indigo-50 text-indigo-700 font-semibold' : ''
                      }`}
                    >
                      <Clock className="w-3 h-3" />
                      <span>Early Action (EA)</span>
                    </button>
                    <button
                      onClick={() => {
                        setFilterRound('regular');
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2 ${
                        filterRound === 'regular' ? 'bg-slate-50 text-slate-700' : ''
                      }`}
                    >
                      <Calendar className="w-3 h-3" />
                      <span>Regular Decision (RD)</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Batching Toggle */}
              <button
                onClick={() => setBatchingEnabled(!batchingEnabled)}
                className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                  batchingEnabled
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-stone-100 text-slate-800'
                }`}
                title={batchingEnabled ? 'Disable task batching' : 'Enable task batching'}
              >
                {batchingEnabled ? 'Batching On' : 'Batching Off'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main view */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl border border-gold-400 p-6 shadow-md">
              {/* View header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                {currentView === 'weekly' && (
                  <>
                  <button
                    onClick={() => setSelectedDate(addDays(selectedDate, -1))}
                    className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4 text-slate-800" />
                  </button>

                  <h2 className="text-lg font-semibold text-slate-900">
                    {`Week of ${format(startOfWeek(selectedDate), 'MMM d')}`}
                  </h2>

                  <button
                    onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                    className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-4 h-4 text-slate-800" />
                  </button>
                  </>
                )}
                </div>

                {/* View toggle */}
                <div className="flex gap-1 p-1 bg-stone-100 rounded-lg">
                  {['focused', 'colleges', 'weekly', 'all'].map(view => (
                    <button
                      key={view}
                      onClick={() => setCurrentView(view)}
                      className={`
                        px-4 py-2 text-sm font-medium rounded-md transition-all
                        ${currentView === view
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-800 hover:text-slate-900'
                        }
                      `}
                    >
                      {view.charAt(0).toUpperCase() + view.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* View content */}
              {currentView === 'focused' && (
                <HierarchicalView
                  timeline={timeline}
                  onTaskClick={(task) => {
                    if (task.module_link) {
                      navigate(task.module_link);
                    }
                  }}
                  onTaskComplete={async (task) => {
                    await toggleTaskStatus(task.id);
                    refetchTimeline();
                  }}
                  onAISupport={(task) => {
                    // Navigate to AI support or open modal
                    console.log('AI support for:', task);
                  }}
                  onFocusMode={() => {
                    setFilterCategory('overdue');
                  }}
                />
              )}
              {currentView === 'colleges' && (
                <CollegesView
                  timeline={timeline}
                  colleges={colleges}
                  onTaskClick={(task) => {
                    if (task.module_link) {
                      navigate(task.module_link);
                    }
                  }}
                  onTaskComplete={async (task) => {
                    await toggleTaskStatus(task.id);
                    refetchTimeline();
                  }}
                  onAISupport={(task) => {
                    console.log('AI support for:', task);
                  }}
                />
              )}
              {currentView === 'weekly' && <WeekView />}
              {currentView === 'all' && (
                <AllView
                  timeline={timeline}
                  allViewTab={allViewTab}
                  setAllViewTab={setAllViewTab}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  filterCategory={filterCategory}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder}
                  selectedTasks={selectedTasks}
                  setSelectedTasks={setSelectedTasks}
                  toggleTaskStatus={toggleTaskStatus}
                  refetchTimeline={refetchTimeline}
                  activeSession={activeSession}
                />
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Week at a glance */}
            <div className="lg:col-span-1">
                <div className="space-y-4">
                  {/* Week at a Glance */}
                  <WeekAtGlance
                    timeline={timeline}
                    contextualIntelligence={contextualIntelligence}
                  />

                  {/* Upcoming Deadlines */}
                  <UpcomingDeadlines
                    colleges={colleges}
                    timeline={timeline}
                    maxDisplay={5}
                    onDeadlineClick={(deadline) => {
                      if (deadline.type === 'view_all') {
                        // Navigate to All view with deadline filter
                        setCurrentView('all');
                      } else if (deadline.college_id) {
                        // Navigate to colleges view
                        setCurrentView('colleges');
                      } else if (deadline.task) {
                        // Handle task click
                        if (deadline.task.module_link) {
                          navigate(deadline.task.module_link);
                        }
                      }
                    }}
                  />
                </div>
              </div>


            {/* Milestone progress */}
            <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-900">
              <h3 className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-3">
                Milestone Progress
              </h3>
              <div className="space-y-3">
                {Object.values(CATEGORIES).map(cat => {
                  const progress = calculateMilestoneProgress(cat.key);
                  const taskCount = timeline.filter(t => t.category === cat.key).length;

                  return (
                    <button
                      key={cat.key}
                      onClick={() => setShowCategoryModal(cat.key)}
                      className="w-full text-left hover:bg-stone-50 p-2 -mx-2 rounded-lg transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-800">
                          {cat.label}
                        </span>
                        <span className="text-xs text-slate-800">
                          {progress}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r transition-all duration-500"
                          style={{
                            width: `${progress}%`,
                            backgroundImage: `linear-gradient(to right, ${
                              cat.key === 'essay' ? '#f59e0b, #fbbf24' :
                              cat.key === 'research' ? '#3b82f6, #60a5fa' :
                              cat.key === 'financial' ? '#10b981, #34d399' :
                              cat.key === 'profile' ? '#ec4899, #f472b6' :
                              cat.key === 'testing' ? '#8b5cf6, #a78bfa' :
                              cat.key === 'decision' ? '#6366f1, #818cf8' :
                              '#8b5cf6, #a78bfa'
                            })`
                          }}
                        />
                      </div>
                      <div className="text-xs text-slate-800 mt-1">
                        {taskCount} tasks
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Adaptive Timeline Support - Consequence-based task triage with progressive disclosure */}
            <AdaptiveTimelineSupport
              timeline={timeline}
              currentDate={new Date()}
              currentUser={currentUser}
              onApplyPlan={(plan) => {
                // Handle MVV applications or other plan actions
                if (plan.type === 'apply_mvv' && plan.tasks) {
                  console.log('Applying MVV for tasks:', plan.tasks);
                  // TODO: Implement MVV application logic
                  toast.success(`Using quick versions for ${plan.tasks.length} tasks - saved time!`);
                  refetchTimeline(); // Refresh timeline
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Category modal */}
      {showCategoryModal && (
        <CategoryModal
          key={`category-modal-${showCategoryModal}`}
          category={showCategoryModal}
          onClose={() => setShowCategoryModal(null)}
        />
      )}

      {/* Task Manager Panel */}
      {showTaskManager && (
        <TaskManagerPanel
          isOpen={showTaskManager}
          onClose={handleCloseTaskManager}
          timeline={timeline}
          onTaskUpdate={handleTaskUpdate}
          userId={currentUser?.id}
        />
      )}

      {/* Smart Action Preview */}
      {showInsightPreview && (
        <SmartActionPreview
          insight={showInsightPreview}
          timeline={timeline}
          onApply={handleInsightAction}
          onDismiss={handleDismissInsight}
          onClose={() => setShowInsightPreview(null)}
        />
      )}

      {/* Recovery Plan Modal */}
      {showRecoveryPlan && (
        <RecoveryPlanErrorBoundary onClose={() => setShowRecoveryPlan(false)}>
          <RecoveryPlanPreview
          overdueTasks={timeline.filter(t => t.isOverdue && !t.isCompleted)}
          upcomingTasks={timeline.filter(t => !t.isOverdue && !t.isCompleted)}
          onApply={async (plan) => {
            try {
              console.log('Applying recovery plan:', plan);

              // Validate plan before applying
              if (!plan?.redistributedTasks?.length) {
                throw new Error('No tasks to reschedule in recovery plan');
              }

              // Store original states for rollback
              const originalTasks = [];
              const taskUpdates = [];

              // Prepare all updates and store originals
              for (const task of plan.redistributedTasks) {
                if (!task.id) {
                  console.warn('Skipping task without ID:', task);
                  continue;
                }

                // Get current task state for rollback
                const { data: currentTask, error: fetchError } = await supabase
                  .from('timeline_items')
                  .select('id, scheduled_date, metadata')
                  .eq('id', task.id)
                  .single();

                if (fetchError) {
                  console.warn(`Could not fetch task ${task.id} for rollback:`, fetchError);
                } else {
                  originalTasks.push(currentTask);
                }

                taskUpdates.push({
                  id: task.id,
                  scheduled_date: task.scheduled_date,
                  metadata: {
                    ...task.metadata,
                    recovery_planned: true,
                    recovery_applied_at: new Date().toISOString(),
                    recovery_plan_id: plan.id
                  }
                });
              }

              // Apply all updates in a single batch
              const { data: updatedTasks, error: updateError } = await supabase
                .from('timeline_items')
                .upsert(taskUpdates, { onConflict: 'id' })
                .select();

              if (updateError) {
                throw new Error(`Database update failed: ${updateError.message}`);
              }

              const successCount = updatedTasks?.length || 0;
              const expectedCount = taskUpdates.length;

              if (successCount !== expectedCount) {
                // Partial failure - attempt rollback
                console.warn(`Partial failure: ${successCount}/${expectedCount} tasks updated`);

                if (originalTasks.length > 0) {
                  console.log('Attempting rollback...');
                  try {
                    await supabase
                      .from('timeline_items')
                      .upsert(originalTasks, { onConflict: 'id' });

                    throw new Error(`Recovery plan partially failed and was rolled back. Please try again.`);
                  } catch (rollbackError) {
                    console.error('Rollback failed:', rollbackError);
                    throw new Error(`Recovery plan partially failed and rollback also failed. Please check your timeline manually.`);
                  }
                }

                throw new Error(`Recovery plan partially failed. ${successCount} of ${expectedCount} tasks were updated.`);
              }

              toast.success(`Recovery plan applied! ${successCount} tasks rescheduled successfully.`);
              setShowRecoveryPlan(null);
              refetchTimeline();

            } catch (error) {
              console.error('Error applying recovery plan:', error);
              toast.error(error.message || 'Failed to apply recovery plan. Please try again.');
            }
          }}
          onClose={() => setShowRecoveryPlan(null)}
        />
        </RecoveryPlanErrorBoundary>
      )}

      {/* Task Creator Modal */}
      {showTaskCreator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <QuickTaskAdd
              date={selectedDate}
              currentUser={currentUser}
              colleges={colleges}
              onTaskAdded={(data) => {
                if (data) {
                  refetchTimeline();
                }
                setShowTaskCreator(false);
              }}
              existingTasks={timeline}
              currentWorkload={getTasksForDateRange(selectedDate, selectedDate).reduce((sum, t) => sum + (t.estimated_hours || 0), 0)}
              position="modal"
            />
          </div>
        </div>
      )}

      {/* Click outside to close menus */}
      {(showRescheduleMenu || showFilterDropdown) && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => {
            setShowRescheduleMenu(null);
            setShowFilterDropdown(false);
          }}
        />
      )}

      {/* Crisis Support Modal */}
      <CrisisSupport
        tasks={timeline}
        userContext={{
          userId: currentUser?.id,
          userEmail: currentUser?.email,
          crisisLevel,
          overdueCount: timeline.filter(t => t.isOverdue && !t.isCompleted).length
        }}
        isVisible={showCrisisSupport}
        onPlanExecuted={(result) => {
          console.log('[Crisis Support] Plan executed:', result);

          // Store undo token
          if (result.undoToken) {
            localStorage.setItem('lastUndoToken', result.undoToken);
          }

          // Show success toast
          toast.success('Recovery plan applied successfully!');

          // Set flag to prevent re-detection immediately
          sessionStorage.setItem('crisis_plan_executed', Date.now());

          // IMMEDIATELY refresh timeline so batches recalculate with new dates
          console.log('[Crisis] Triggering immediate timeline refetch to update batches');
          refetchTimeline();

          // Hide crisis modal after success message
          setTimeout(() => {
            setShowCrisisSupport(false);
          }, 5000); // Match the "Closing in 5 seconds" message
        }}
        onClose={() => setShowCrisisSupport(false)}
      />

      {/* Date Picker Modal */}
      {showDatePicker && datePickerTaskId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Choose New Date
            </h3>
            <input
              type="date"
              min={format(new Date(), 'yyyy-MM-dd')}
              onChange={(e) => {
                if (e.target.value) {
                  rescheduleTask(datePickerTaskId, parseISO(e.target.value));
                  setShowDatePicker(false);
                  setDatePickerTaskId(null);
                }
              }}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
              autoFocus
            />
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  setShowDatePicker(false);
                  setDatePickerTaskId(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedTimelineIntegrated;